"use client"

import { useEffect, useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import {
  TrendingUp, Download, RefreshCw, Star, DollarSign, Users, Briefcase,
  Target, AlertCircle, ArrowUp, ArrowDown, Workflow, Plus, Trash2, Receipt, Megaphone,
  Pencil, RotateCcw, X, Zap,
} from "lucide-react"
import { useAuth } from "../context/AuthContext"
import { useLocationScope } from "../context/LocationContext"
import {
  jobsAPI, customersAPI, teamAPI, invoicesAPI, payrollAPI, analyticsAPI, businessExpensesAPI, marketingSpendAPI,
} from "../services/api"
import { normalizeAPIResponse } from "../utils/dataHandler"
import { isAccountOwner } from "../utils/roleUtils"
import MobileHeader from "../components/mobile-header"
import {
  SfCard, SfCardHeader, SfButton, SfPageHeader, SfTab, SfKPI, SfAvatar, sfInitials,
} from "../components/sf-primitives"

// ── Tokens (mirrored from design pack §T) ───────────────────────────────
const T = {
  ink:        "var(--sf-ink)",
  ink2:       "var(--sf-ink-2)",
  ink3:       "var(--sf-ink-3)",
  ink4:       "var(--sf-ink-4, #94a3b8)",
  panel:      "var(--sf-panel)",
  panelSoft:  "var(--sf-panel-soft)",
  panelAlt:   "var(--sf-panel-alt, #f8fafc)",
  border:     "var(--sf-border)",
  borderS:    "var(--sf-border-soft)",
  blue:       "var(--sf-blue)",
  blueSoft:   "var(--sf-blue-soft)",
  blueDark:   "var(--sf-blue-dark)",
  green:      "var(--sf-green)",
  greenSoft:  "var(--sf-green-soft)",
  greenDark:  "var(--sf-green-dark)",
  amber:      "var(--sf-amber)",
  amberSoft:  "var(--sf-amber-soft)",
  amberDark:  "var(--sf-amber-dark)",
  red:        "var(--sf-red)",
  redSoft:    "var(--sf-red-soft)",
  redDark:    "var(--sf-red-dark)",
  purple:     "var(--sf-purple, #8b5cf6)",
  purpleSoft: "var(--sf-purple-soft, #ede9fe)",
  teal:       "var(--sf-teal, #14b8a6)",
  tealSoft:   "var(--sf-teal-soft, #ccfbf1)",
}

// Translucent version of a theme colour. Theme colours are CSS vars, so the
// usual "#rrggbb" + "22" hex-alpha trick produces invalid CSS ("var(--x)22")
// and the browser drops the whole property. color-mix works with vars.
const alpha = (color, hexAlpha) =>
  `color-mix(in srgb, ${color} ${Math.round((parseInt(hexAlpha, 16) / 255) * 100)}%, transparent)`

// ── Date range helpers ─────────────────────────────────────────────────
const PERIODS = {
  "7d":  { days: 7,   label: "Last 7 days" },
  "30d": { days: 30,  label: "Last 30 days" },
  "90d": { days: 90,  label: "Last 90 days" },
  "ytd": { days: -1,  label: "Year to date" },
  "all": { days: 730, label: "All time" },
}

const fmtDateLocal = (d) => {
  const y = d.getFullYear(), m = String(d.getMonth() + 1).padStart(2, "0"), day = String(d.getDate()).padStart(2, "0")
  return `${y}-${m}-${day}`
}

// Parse a "YYYY-MM-DD" (or "YYYY-MM-DD HH:MM:SS") string as LOCAL midnight,
// not UTC. new Date("2026-10-24") is parsed as UTC — which shifts west-of-UTC
// tenants back a day, so Friday jobs land in Thursday's bucket on the weekday
// chart and the daily revenue trend.
const parseLocalDate = (ds) => {
  if (!ds) return null
  const datePart = String(ds).split(" ")[0].split("T")[0]
  const parts = datePart.split("-").map((p) => parseInt(p, 10))
  if (parts.length !== 3 || parts.some((n) => !Number.isFinite(n))) return null
  const [y, m, d] = parts
  const dt = new Date(y, m - 1, d)
  return Number.isNaN(dt.getTime()) ? null : dt
}

const rangeFor = (period) => {
  const end = new Date(); end.setHours(23, 59, 59, 999)
  const start = new Date()
  if (period === "ytd") {
    start.setMonth(0, 1)
  } else {
    const days = PERIODS[period]?.days ?? 30
    start.setDate(start.getDate() - (days - 1))
  }
  start.setHours(0, 0, 0, 0)
  return { start, end, startStr: fmtDateLocal(start), endStr: fmtDateLocal(end) }
}

const moneyShort = (v) => {
  const n = Number(v) || 0
  if (Math.abs(n) >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`
  if (Math.abs(n) >= 1_000) return `$${(n / 1_000).toFixed(1)}k`
  return `$${Math.round(n)}`
}
const money = (v) => `$${Math.round(Number(v) || 0).toLocaleString()}`
const pct = (v, total) => (total > 0 ? Math.round((v / total) * 100) : 0)
const jobPrice = (j) =>
  parseFloat(j?.total_amount) || parseFloat(j?.total) ||
  parseFloat(j?.service_price) || parseFloat(j?.price) ||
  parseFloat(j?.invoice_amount) || 0

// ══════════════════════════════════════════════════════════════════════
// CHART PRIMITIVES
// ══════════════════════════════════════════════════════════════════════

const BarChart = ({ data, labels, height = 200, color = T.blue, valueFmt = (v) => v }) => {
  const max = Math.max(1, ...data)
  // Density: label every bar when it fits (<=12 points); otherwise show
  // Y-axis min/mid/max on the left so the magnitude is still readable.
  const dense = data.length <= 12
  // Reserve pixel room for the x-axis label row + value chip. Bars draw
  // in pure pixels so flex-column % heights never collapse.
  const LABEL_ROW_PX = 18
  const VALUE_ROW_PX = dense ? 16 : 0
  const BAR_AREA_PX = Math.max(40, height - LABEL_ROW_PX - VALUE_ROW_PX)
  return (
    <div style={{ display: "flex", gap: 8 }}>
      {!dense && (
        <div style={{
          display: "flex", flexDirection: "column", justifyContent: "space-between",
          height: BAR_AREA_PX + VALUE_ROW_PX, paddingTop: 2, paddingBottom: 2,
          fontSize: 10, color: T.ink3, fontWeight: 600, fontVariantNumeric: "tabular-nums",
        }}>
          <span>{valueFmt(max)}</span>
          <span>{valueFmt(Math.round(max / 2))}</span>
          <span>0</span>
        </div>
      )}
      <div style={{ flex: 1 }}>
        <div style={{ display: "flex", alignItems: "flex-end", gap: dense ? 10 : 4 }}>
          {data.map((v, i) => {
            const barPx = Math.max(v > 0 ? 2 : 0, Math.round((v / max) * BAR_AREA_PX))
            const showValue = v > 0 && (dense || i === data.length - 1)
            return (
              <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
                <div style={{ height: VALUE_ROW_PX, display: "flex", alignItems: "flex-end", fontSize: 10, fontWeight: 700, color: T.ink, fontVariantNumeric: "tabular-nums" }}>
                  {showValue ? valueFmt(v) : ""}
                </div>
                <div style={{
                  width: "100%",
                  height: barPx,
                  background: color,
                  opacity: i === data.length - 1 ? 1 : 0.65,
                  borderRadius: "4px 4px 0 0",
                  transition: "height .3s",
                }} />
                <div style={{ height: LABEL_ROW_PX, fontSize: 10, color: T.ink3, fontWeight: 500 }}>{labels[i]}</div>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

const LineAreaChart = ({ data, labels, height = 200, color = T.blue, gradId = "lg", valueFmt = (v) => v }) => {
  const max = Math.max(...data, 1), min = Math.min(...data, 0)
  const range = max - min || 1
  const width = 100
  const step = data.length > 1 ? width / (data.length - 1) : 0
  const pts = data.map((v, i) => [i * step, 100 - ((v - min) / range) * 90 - 5])
  const path = pts.map((p, i) => `${i === 0 ? "M" : "L"}${p[0]},${p[1]}`).join(" ")
  // Peak + last point annotations so values are visible without hover.
  const maxIdx = data.indexOf(max)
  const lastIdx = data.length - 1
  const annotIndices = Array.from(new Set([maxIdx, lastIdx])).filter((i) => i >= 0 && data[i] > 0)
  const Y_LABELS = [max, min + range * 0.5, min]
  return (
    <div style={{ display: "flex", gap: 8 }}>
      <div style={{
        display: "flex", flexDirection: "column", justifyContent: "space-between",
        height, paddingTop: 2, paddingBottom: 20,
        fontSize: 10, color: T.ink3, fontWeight: 600, fontVariantNumeric: "tabular-nums",
      }}>
        {Y_LABELS.map((y, i) => <span key={i}>{valueFmt(Math.round(y))}</span>)}
      </div>
      <div style={{ position: "relative", height, flex: 1 }}>
        <svg width="100%" height="100%" viewBox={`0 0 ${width} 100`} preserveAspectRatio="none">
          <defs>
            <linearGradient id={gradId} x1="0" x2="0" y1="0" y2="1">
              <stop offset="0%" stopColor={color} stopOpacity=".25" />
              <stop offset="100%" stopColor={color} stopOpacity="0" />
            </linearGradient>
          </defs>
          <path d={`${path} L${width},100 L0,100 Z`} fill={`url(#${gradId})`} />
          <path d={path} stroke={color} strokeWidth="1.5" fill="none" vectorEffect="non-scaling-stroke" strokeLinecap="round" />
          {annotIndices.map((i) => (
            <circle key={i} cx={pts[i][0]} cy={pts[i][1]} r="1.6" fill={color} vectorEffect="non-scaling-stroke" />
          ))}
        </svg>
        {annotIndices.map((i) => (
          <div key={i} style={{
            position: "absolute",
            left: `${pts[i][0]}%`, top: `calc(${pts[i][1]}% - 18px)`,
            transform: "translateX(-50%)",
            fontSize: 10, fontWeight: 700, color: T.ink,
            fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap",
            background: T.panel, padding: "0 3px", borderRadius: 2,
            pointerEvents: "none",
          }}>
            {valueFmt(data[i])}
          </div>
        ))}
        <div style={{
          position: "absolute", left: 0, right: 0, bottom: -18,
          display: "flex", justifyContent: "space-between",
          fontSize: 10, color: T.ink3, fontWeight: 500,
        }}>
          {labels.map((l, i) =>
            (i === 0 || i === Math.floor(labels.length / 2) || i === labels.length - 1)
              ? <span key={i}>{l}</span>
              : <span key={i} style={{ visibility: "hidden" }}>·</span>
          )}
        </div>
      </div>
    </div>
  )
}

const DonutChart = ({ data, size = 120, label = "Total", valueFmt = (v) => v.toLocaleString(), showLegend = false }) => {
  const total = data.reduce((s, d) => s + d.v, 0) || 1
  const r = size / 2 - 8
  const C = 2 * Math.PI * r
  let offset = 0
  const sum = data.reduce((s, d) => s + d.v, 0)
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
      <div style={{ position: "relative", width: size, height: size, flexShrink: 0 }}>
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
          <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={T.panelSoft} strokeWidth="10" />
          {data.map((d, i) => {
            const len = (d.v / total) * C
            const seg = (
              <circle
                key={i}
                cx={size / 2} cy={size / 2} r={r}
                fill="none" stroke={d.c} strokeWidth="10"
                strokeDasharray={`${len} ${C}`}
                strokeDashoffset={-offset}
                transform={`rotate(-90 ${size / 2} ${size / 2})`}
              />
            )
            offset += len
            return seg
          })}
        </svg>
        <div style={{
          position: "absolute", inset: 0, display: "flex",
          flexDirection: "column", alignItems: "center", justifyContent: "center",
        }}>
          <div style={{
            fontSize: 20, fontWeight: 700, color: T.ink,
            letterSpacing: "-0.015em", fontVariantNumeric: "tabular-nums",
          }}>{valueFmt(sum)}</div>
          <div style={{
            fontSize: 10, color: T.ink3, fontWeight: 600,
            letterSpacing: ".04em", textTransform: "uppercase",
          }}>{label}</div>
        </div>
      </div>
      {showLegend && data.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 6, minWidth: 0, flex: 1 }}>
          {data.map((d, i) => {
            const pct = sum > 0 ? Math.round((d.v / sum) * 100) : 0
            return (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 11.5 }}>
                <span style={{ width: 10, height: 10, borderRadius: 2, background: d.c, flexShrink: 0 }} />
                <span style={{
                  flex: 1, color: T.ink, fontWeight: 500,
                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                }}>{d.l || d.name || `Series ${i + 1}`}</span>
                <span style={{ color: T.ink2, fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>{valueFmt(d.v)}</span>
                <span style={{ color: T.ink3, fontWeight: 600, fontVariantNumeric: "tabular-nums", width: 36, textAlign: "right" }}>
                  {pct}%
                </span>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

const HBarList = ({ data, color = T.blue, fmt = (v) => v }) => {
  const max = Math.max(1, ...data.map((d) => d.v))
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 11 }}>
      {data.map((r, i) => (
        <div key={i}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, marginBottom: 4 }}>
            <span style={{ flex: 1, color: T.ink, fontWeight: 600 }}>{r.l}</span>
            <span style={{ color: T.ink2, fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>{fmt(r.v)}</span>
          </div>
          <div style={{ height: 6, background: T.panelSoft, borderRadius: 3, overflow: "hidden" }}>
            <div style={{ width: `${(r.v / max) * 100}%`, height: "100%", background: r.c || color, borderRadius: 3 }} />
          </div>
        </div>
      ))}
    </div>
  )
}

const MiniSpark = ({ data, color = T.green, width = 88, height = 24 }) => {
  if (!data?.length) return null
  const max = Math.max(...data), min = Math.min(...data)
  const range = max - min || 1
  const step = width / (data.length - 1 || 1)
  const path = data
    .map((v, i) => `${i === 0 ? "M" : "L"}${i * step},${height - ((v - min) / range) * height}`)
    .join(" ")
  return (
    <svg width={width} height={height}>
      <path d={path} stroke={color} strokeWidth="1.5" fill="none" strokeLinecap="round" />
    </svg>
  )
}

const Mini = ({ label, value, sub }) => (
  <div style={{ flex: 1, padding: "4px 0" }}>
    <div style={{ fontSize: 10, color: T.ink3, fontWeight: 700, letterSpacing: ".04em", textTransform: "uppercase" }}>
      {label}
    </div>
    <div style={{ fontSize: 14, fontWeight: 700, color: T.ink, marginTop: 2, fontVariantNumeric: "tabular-nums" }}>
      {value}
    </div>
    {sub && <div style={{ fontSize: 10.5, color: T.ink3, marginTop: 1 }}>{sub}</div>}
  </div>
)

const DualBarChart = ({ revenue, payroll, labels, height = 200 }) => {
  const maxRev = Math.max(1, ...revenue)
  // Pixel heights so flex-column % collapse can't flatten the bars.
  const VALUE_ROW_PX = 14
  const RATIO_LABEL_PX = 16
  const DAY_LABEL_PX = 14
  const BAR_AREA_PX = Math.max(60, height - VALUE_ROW_PX - RATIO_LABEL_PX - DAY_LABEL_PX)
  return (
    <div style={{ paddingTop: 14, paddingBottom: 6 }}>
      <div style={{ display: "flex", alignItems: "flex-end", gap: 14 }}>
        {revenue.map((r, i) => {
          const p = payroll[i] || 0
          const ratio = r > 0 ? (p / r) * 100 : 0
          const rPx = Math.max(r > 0 ? 2 : 0, Math.round((r / maxRev) * BAR_AREA_PX))
          const pPx = Math.max(p > 0 ? 2 : 0, Math.round((p / maxRev) * BAR_AREA_PX))
          return (
            <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 5 }}>
              <div style={{ height: BAR_AREA_PX, width: "100%", display: "flex", alignItems: "flex-end", gap: 4 }}>
                <div style={{
                  flex: 1, height: rPx, background: T.green,
                  borderRadius: "4px 4px 0 0", position: "relative",
                }}>
                  {r > 0 && (
                    <div style={{
                      position: "absolute", top: -14, left: "50%", transform: "translateX(-50%)",
                      fontSize: 9, fontWeight: 700, color: T.greenDark,
                      fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap",
                    }}>{moneyShort(r)}</div>
                  )}
                </div>
                <div style={{
                  flex: 1, height: pPx, background: T.amberDark,
                  borderRadius: "4px 4px 0 0", position: "relative",
                }}>
                  {p > 0 && (
                    <div style={{
                      position: "absolute", top: -14, left: "50%", transform: "translateX(-50%)",
                      fontSize: 9, fontWeight: 700, color: T.amberDark,
                      fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap",
                    }}>{moneyShort(p)}</div>
                  )}
                </div>
              </div>
              <div style={{ fontSize: 10.5, fontWeight: 700, color: T.blueDark, fontVariantNumeric: "tabular-nums" }}>
                {ratio.toFixed(0)}%
              </div>
              <div style={{ fontSize: 10, color: T.ink3, fontWeight: 500 }}>{labels[i]}</div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

// Empty state placeholder used inside cards when a slice has no data
const EmptyChart = ({ icon: Icon = AlertCircle, title, subtitle, height = 180 }) => (
  <div style={{
    height, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
    gap: 6, color: T.ink3, textAlign: "center",
  }}>
    <Icon size={24} strokeWidth={1.5} />
    <div style={{ fontSize: 12.5, fontWeight: 600, color: T.ink2 }}>{title}</div>
    {subtitle && <div style={{ fontSize: 11.5, color: T.ink3 }}>{subtitle}</div>}
  </div>
)

// ══════════════════════════════════════════════════════════════════════
// DATA FETCHING
// ══════════════════════════════════════════════════════════════════════

async function fetchEverything(userId, period, locationId) {
  const { start, end, startStr, endStr } = rangeFor(period)

  // Jobs are loaded once for max(selected period, last ~13 months) and then
  // filtered to the period here. The 12-week mix, 6-month cohort grid and
  // reactivation check need history beyond a 7/30-day window.
  const histStart = new Date(Math.min(start.getTime(), Date.now() - 400 * 24 * 60 * 60 * 1000))
  histStart.setHours(0, 0, 0, 0)
  const histStartStr = fmtDateLocal(histStart)

  const safe = async (p, fb) => { try { return await p } catch { return fb } }

  const [jobsResp, invoicesResp, customersResp, teamResp, salaryData, conversionData, recurringData, lostData, adsSpendData, expensesSummaryData, businessExpensesResp, marketingSpendResp, reviewsData, paymentsSummary] = await Promise.all([
    safe(jobsAPI.getAll(userId, "", "", 1, 20000, null, `${histStartStr} to ${endStr}`), { jobs: [] }),
    // limits above 1000 make the backend page through every row
    safe(invoicesAPI.getAll(userId, { page: 1, limit: 20000 }), { invoices: [] }),
    safe(customersAPI.getAll(userId, { page: 1, limit: 10000 }), { customers: [] }),
    safe(teamAPI.getAll(userId, { page: 1, limit: 1000 }), { teamMembers: [] }),
    safe(payrollAPI.getSalaryAnalytics(startStr, endStr, "month"), { timeSeries: [], memberBreakdown: [], summary: {} }),
    safe(analyticsAPI.getConversionMetrics(startStr, endStr, "week"), { summary: {}, bySource: {}, byStage: {}, timeSeries: [] }),
    safe(analyticsAPI.getRecurringConversionMetrics(startStr, endStr, "week"), { summary: {}, byFrequency: {}, timeSeries: [], customerBreakdown: [] }),
    safe(analyticsAPI.getLostCustomersMetrics(startStr, endStr, "week", 90), { summary: {}, timeSeries: [], lostCustomersList: [] }),
    safe(analyticsAPI.getAdsSpend(startStr, endStr, locationId), { summary: {}, bySource: [], monthly: [] }),
    safe(analyticsAPI.getExpensesSummary(startStr, endStr), { summary: {}, jobExpensesByType: {}, businessByCategory: {}, businessExpanded: [] }),
    safe(businessExpensesAPI.getAll(), { expenses: [] }),
    safe(marketingSpendAPI.getAll({ startDate: startStr, endDate: endStr, locationId }), { spend: [] }),
    safe(analyticsAPI.getReviews(startStr, endStr), { reviews: [] }),
    safe(analyticsAPI.getPaymentsSummary(startStr, endStr), null),
  ])

  const historyJobs = normalizeAPIResponse(jobsResp, "jobs") || []
  // Same rule as the backend's date filter: scheduled_date's day within
  // [startStr, endStr].
  const jobs = historyJobs.filter((j) => {
    const day = String(j.scheduled_date || "").slice(0, 10)
    return day >= startStr && day <= endStr
  })
  // Merge reviews onto jobs so downstream rating logic (avg rating, Reviewed
  // funnel stage, Quality issues) reads from a single `j.customer_rating`
  // field regardless of whether it came from a job column or the reviews
  // table. One review per job wins (highest rating → best-case for ties).
  const reviewsByJob = {}
  for (const r of (reviewsData?.reviews || [])) {
    if (!r.job_id) continue
    const existing = reviewsByJob[r.job_id]
    if (!existing || Number(r.rating) > Number(existing.rating)) reviewsByJob[r.job_id] = r
  }
  for (const j of jobs) {
    const r = reviewsByJob[j.id]
    if (r && r.rating != null && j.customer_rating == null) j.customer_rating = r.rating
  }
  const allInvoices = invoicesResp.invoices || []
  const invoices = allInvoices.filter((inv) => {
    const d = new Date(inv.created_at)
    return d >= start && d <= end
  })
  const customers = Array.isArray(customersResp)
    ? customersResp
    : customersResp.customers || customersResp.data || []
  const teamMembers = teamResp.teamMembers || []

  return {
    start, end, period, jobs, historyJobs, invoices, allInvoices, customers, teamMembers,
    salaryData, conversionData, recurringData, lostData, paymentsSummary,
    adsSpendData, expensesSummaryData,
    businessExpenses: businessExpensesResp.expenses || [],
    marketingSpend: marketingSpendResp.spend || [],
    dateRange: { startStr, endStr },
  }
}

// ══════════════════════════════════════════════════════════════════════
// DERIVED METRICS
// ══════════════════════════════════════════════════════════════════════

const isCancelledJob = (j) => ["cancelled", "canceled"].includes(String(j?.status || "").toLowerCase())
// Invoices that represent real billed revenue (not voided, not unsent drafts).
const isBilledInvoice = (inv) => !["void", "draft", "refunded"].includes(String(inv?.status || "").toLowerCase())
const IN_PROGRESS_STATUSES = new Set(["in-progress", "in_progress", "started", "en-route", "en_route"])
const jobDurationMin = (j) => Number(j?.duration) || Number(j?.service_duration) || 0

function computeMetrics(d) {
  if (!d) return null

  // Cancelled jobs carry a price but earn nothing — keep them out of revenue.
  const billableJobs = d.jobs.filter((j) => !isCancelledJob(j))

  // ── Revenue
  // Job revenue is the operational truth: every completed/scheduled job has a
  // price even when the invoice hasn't been raised yet. Invoice revenue is for
  // cash-collection metrics only. Using max() so we never under-report.
  const invoiceRevenue = d.invoices.filter(isBilledInvoice).reduce((s, inv) => s + (parseFloat(inv.total_amount) || parseFloat(inv.amount) || 0), 0)
  const jobRevenue = billableJobs.reduce((s, j) => s + jobPrice(j), 0)
  const totalRevenue = Math.max(invoiceRevenue, jobRevenue)

  // ── Job counts
  // "Completed" is the operational truth — explicitly completed/paid, OR a
  // job whose scheduled_date is in the past and isn't cancelled or still
  // pending. That matches what the owner means by "jobs done" and avoids
  // under-counting when status fields lag.
  const nowMs = Date.now()
  const completedJobs = d.jobs.filter((j) => {
    const s = (j.status || "").toLowerCase()
    if (s === "completed" || s === "paid") return true
    if (s === "cancelled" || s === "pending") return false
    const dt = parseLocalDate(j.scheduled_date)
    if (!dt) return false
    return dt.getTime() <= nowMs
  })
  const scheduledCount = d.jobs.length
  const completedCount = completedJobs.length
  const cancelledCount = d.jobs.filter(isCancelledJob).length
  const pendingCount = d.jobs.filter((j) => String(j.status || "").toLowerCase() === "pending").length
  // DB enum is hyphenated ('in-progress', 'en-route'); Zenbooker also writes 'started'.
  const inProgressCount = d.jobs.filter((j) => IN_PROGRESS_STATUSES.has(String(j.status || "").toLowerCase())).length
  const confirmedCount = d.jobs.filter((j) => String(j.status || "").toLowerCase() === "confirmed").length

  const completedWithPrice = completedJobs.filter((j) => jobPrice(j) > 0)
  const avgJobValue = completedWithPrice.length
    ? completedWithPrice.reduce((s, j) => s + jobPrice(j), 0) / completedWithPrice.length
    : 0
  // Average the job's own duration (falls back to the service template's);
  // jobs with no duration are left out instead of dragging the mean to 0.
  const jobsWithDuration = billableJobs.filter((j) => jobDurationMin(j) > 0)
  const avgDurationMin = jobsWithDuration.length
    ? jobsWithDuration.reduce((s, j) => s + jobDurationMin(j), 0) / jobsWithDuration.length
    : 0

  // ── Tips, incentives, ratings, refunds — all read from real fields
  const totalTips = d.jobs.reduce((s, j) => s + (parseFloat(j.tip_amount) || 0), 0)
  const totalIncentives = d.jobs.reduce((s, j) => s + (parseFloat(j.incentive_amount) || 0), 0)
  const ratedJobs = d.jobs.filter((j) => {
    const r = parseFloat(j.customer_rating ?? j.rating)
    return r > 0
  })
  const avgRating = ratedJobs.length
    ? ratedJobs.reduce((s, j) => s + (parseFloat(j.customer_rating ?? j.rating) || 0), 0) / ratedJobs.length
    : 0
  const refundedInvoices = d.allInvoices.filter((inv) => (inv.status || "").toLowerCase() === "refunded")
  const refundedInPeriod = refundedInvoices.filter((inv) => {
    const dt = new Date(inv.updated_at || inv.created_at)
    return dt >= d.start && dt <= d.end
  })
  const refundAmount = refundedInPeriod.reduce((s, inv) =>
    s + (parseFloat(inv.refund_amount) || parseFloat(inv.total_amount) || parseFloat(inv.amount) || 0), 0)

  // ── Customers
  const thirtyDaysAgo = new Date()
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30)
  // Use parseLocalDate so bare-date created_at values ("2026-10-25") don't
  // flip to the prior UTC day for west-of-UTC viewers.
  const newCustomers = d.customers.filter((c) => {
    if (!c.created_at) return false
    const dt = parseLocalDate(String(c.created_at).split(" ")[0]) || new Date(c.created_at)
    return dt >= thirtyDaysAgo
  }).length
  const activeCustomers = d.customers.filter((c) => {
    const s = (c.status || "").toLowerCase()
    return !s || s === "active"
  }).length
  // Repeat = customers with >1 job in window; denominator is in-period
  // customer count (how many distinct customers transacted this period),
  // not all-time active — otherwise rate drifts to 0 on short windows.
  const jobsByCustomer = {}
  d.jobs.forEach((j) => { if (j.customer_id) jobsByCustomer[j.customer_id] = (jobsByCustomer[j.customer_id] || 0) + 1 })
  const inPeriodCustomerCount = Object.keys(jobsByCustomer).length
  const repeatCustomers = Object.values(jobsByCustomer).filter((n) => n > 1).length
  const repeatRate = inPeriodCustomerCount > 0 ? Math.round((repeatCustomers / inPeriodCustomerCount) * 100) : 0

  // Lifetime value — compute from ALL invoices, not just in-period jobs.
  // The KPI subtitle says "lifetime spend"; an in-period-only average was
  // showing one month's spend as "lifetime".
  const ltvByCustomerAllTime = {}
  d.allInvoices.filter(isBilledInvoice).forEach((inv) => {
    if (!inv.customer_id) return
    const v = parseFloat(inv.total_amount) || parseFloat(inv.amount) || 0
    ltvByCustomerAllTime[inv.customer_id] = (ltvByCustomerAllTime[inv.customer_id] || 0) + v
  })
  // Fall back to in-period job revenue when the tenant has no invoices at all.
  const ltvByCustomer = Object.keys(ltvByCustomerAllTime).length ? ltvByCustomerAllTime : (() => {
    const m2 = {}
    billableJobs.forEach((j) => {
      if (!j.customer_id) return
      m2[j.customer_id] = (m2[j.customer_id] || 0) + jobPrice(j)
    })
    return m2
  })()
  const ltvVals = Object.values(ltvByCustomer)
  const avgLTV = ltvVals.length ? ltvVals.reduce((s, v) => s + v, 0) / ltvVals.length : 0

  // Jobs-by-customer across ALL invoices so Top Customers can label a
  // Returning vs One-time customer by lifetime activity, not just the
  // in-period slice.
  const jobsByCustomerAllTime = {}
  d.allInvoices.filter(isBilledInvoice).forEach((inv) => {
    if (!inv.customer_id) return
    jobsByCustomerAllTime[inv.customer_id] = (jobsByCustomerAllTime[inv.customer_id] || 0) + 1
  })

  // ── Daily revenue series across the period for the line chart
  const dailyMap = {}
  const dayCursor = new Date(d.start)
  while (dayCursor <= d.end) {
    dailyMap[fmtDateLocal(dayCursor)] = 0
    dayCursor.setDate(dayCursor.getDate() + 1)
  }
  // Daily trend reflects the operational revenue — same source as the
  // headline KPI, scheduled-job pricing. Invoice spikes lag and would
  // create misleading single-day peaks.
  const seedFromInvoices = invoiceRevenue > jobRevenue
  if (seedFromInvoices) {
    d.invoices.filter(isBilledInvoice).forEach((inv) => {
      const k = fmtDateLocal(new Date(inv.created_at))
      if (k in dailyMap) dailyMap[k] += (parseFloat(inv.total_amount) || parseFloat(inv.amount) || 0)
    })
  } else {
    billableJobs.forEach((j) => {
      const ds = j.scheduled_date ? String(j.scheduled_date).split(" ")[0] : null
      if (ds && ds in dailyMap) dailyMap[ds] += jobPrice(j)
    })
  }
  const daily = Object.entries(dailyMap).sort(([a], [b]) => a.localeCompare(b))
  const dailyValues = daily.map(([, v]) => v)
  const dailyLabels = daily.map(([k]) => {
    const dt = parseLocalDate(k) || new Date(k)
    return `${dt.toLocaleString("en-US", { month: "short" })} ${dt.getDate()}`
  })

  // ── Monthly revenue: last 7 months
  const monthly = []
  const monthCursor = new Date(); monthCursor.setDate(1)
  for (let i = 0; i < 7; i++) {
    const m = new Date(monthCursor); m.setMonth(m.getMonth() - i)
    monthly.unshift({
      key: `${m.getFullYear()}-${m.getMonth()}`,
      label: m.toLocaleString("en-US", { month: "short" }),
      revenue: 0,
    })
  }
  d.allInvoices.filter(isBilledInvoice).forEach((inv) => {
    const dt = new Date(inv.created_at)
    const k = `${dt.getFullYear()}-${dt.getMonth()}`
    const row = monthly.find((r) => r.key === k)
    if (row) row.revenue += (parseFloat(inv.total_amount) || parseFloat(inv.amount) || 0)
  })
  // Fallback: when the tenant has no invoices at all, roll monthly totals
  // from job pricing so the Revenue tab's 7-month chart isn't a flat line.
  // Uses the ~13-month job history — the period's jobs only cover 7/30 days.
  const monthlyHasInvoiceRevenue = monthly.some((r) => r.revenue > 0)
  if (!monthlyHasInvoiceRevenue) {
    (d.historyJobs || d.jobs).filter((j) => !isCancelledJob(j)).forEach((j) => {
      const dt = parseLocalDate(j.scheduled_date)
      if (!dt) return
      const k = `${dt.getFullYear()}-${dt.getMonth()}`
      const row = monthly.find((r) => r.key === k)
      if (row) row.revenue += jobPrice(j)
    })
  }
  const monthlySource = monthlyHasInvoiceRevenue ? "invoices" : "job pricing"

  // ── Weekday distribution
  const weekday = [0, 0, 0, 0, 0, 0, 0]
  billableJobs.forEach((j) => {
    const dt = parseLocalDate(j.scheduled_date)
    if (!dt) return
    // Map JS getDay (0=Sun..6=Sat) → Mon=0..Sun=6
    const jsDay = dt.getDay()
    const idx = jsDay === 0 ? 6 : jsDay - 1
    weekday[idx] += 1
  })

  // ── Service distribution
  const serviceCounts = {}
  billableJobs.forEach((j) => {
    const name = j.service_name || "Other"
    serviceCounts[name] = (serviceCounts[name] || 0) + 1
  })
  const services = Object.entries(serviceCounts)
    .map(([name, count]) => ({ name, count, revenue: 0 }))
    .sort((a, b) => b.count - a.count)
  billableJobs.forEach((j) => {
    const row = services.find((s) => s.name === (j.service_name || "Other"))
    if (row) row.revenue += jobPrice(j)
  })

  // ── Territory distribution
  const territoryCounts = {}
  billableJobs.forEach((j) => {
    const name = j.territory_name || j.territory || j.location_name || "Unassigned"
    territoryCounts[name] = (territoryCounts[name] || 0) + 1
  })
  const territories = Object.entries(territoryCounts)
    .map(([l, v]) => ({ l, v }))
    .sort((a, b) => b.v - a.v)
    .slice(0, 6)

  // ── Lead source distribution
  const sourceCounts = {}
  d.customers.forEach((c) => {
    const s = c.lead_source || c.source || "Other"
    sourceCounts[s] = (sourceCounts[s] || 0) + 1
  })
  const sources = Object.entries(sourceCounts)
    .map(([l, v]) => ({ l, v }))
    .sort((a, b) => b.v - a.v)
    .slice(0, 6)

  // ── Cancellation reasons
  const cancelReasons = {}
  d.jobs.filter(isCancelledJob).forEach((j) => {
    const r = j.cancellation_reason || j.cancel_reason || "Other"
    cancelReasons[r] = (cancelReasons[r] || 0) + 1
  })
  const cancelList = Object.entries(cancelReasons)
    .map(([l, v]) => ({ l, v }))
    .sort((a, b) => b.v - a.v)

  // ── AR + cash
  // Prefer the backend's payments summary: it sums real recorded payments
  // (transactions) — partial payments and job-level/imported payments
  // included — and computes open balances net of what's been paid.
  // Fallback (older backend): paid invoices + open invoice totals.
  const ps = d.paymentsSummary
  const openInvoices = d.allInvoices.filter((inv) => ["sent", "partial", "pending", "unpaid", "overdue"].includes((inv.status || "").toLowerCase()))
  const cashCollected = ps ? ps.cashCollected
    : d.invoices.filter((inv) => (inv.status || "").toLowerCase() === "paid")
      .reduce((s, inv) => s + (parseFloat(inv.total_amount) || parseFloat(inv.amount) || 0), 0)
  const outstandingAR = ps ? ps.outstandingAR
    : openInvoices.reduce((s, inv) => s + (parseFloat(inv.total_amount) || parseFloat(inv.amount) || 0), 0)
  const outstandingCount = ps ? ps.outstandingCount : openInvoices.length
  const tipsCollected = ps ? ps.tipsCollected : null
  const paymentsCount = ps ? ps.paymentsCount : null

  // ── Shared helpers used by teamRows + topCustomers below AND by the
  // utilization block further down. Defined up here so .map callbacks
  // can close over them without hitting a TDZ.
  // start is 00:00 and end is 23:59:59.999, so the rounded difference already
  // counts both ends (a 30-day period is 30, not 31).
  const periodDays = Math.max(1, Math.round((d.end - d.start) / (24 * 60 * 60 * 1000)))
  const weeksInPeriod = Math.max(1, periodDays / 7)
  const SPARK_BUCKETS = 8
  const bucketIndexFor = (date) => {
    const t = date instanceof Date ? date.getTime() : new Date(date).getTime()
    if (!Number.isFinite(t)) return -1
    const totalMs = Math.max(1, d.end.getTime() - d.start.getTime())
    const idx = Math.floor(((t - d.start.getTime()) / totalMs) * SPARK_BUCKETS)
    return Math.max(0, Math.min(SPARK_BUCKETS - 1, idx))
  }

  // ── Team performance with real revenue per member
  const teamRows = d.teamMembers.map((m) => {
    const memberId = Number(m.id)
    const memberJobs = billableJobs.filter((j) => {
      const direct = Number(j.team_member_id) === memberId || Number(j.assigned_team_member_id) === memberId
      if (direct) return true
      if (Array.isArray(j.team_assignments)) {
        return j.team_assignments.some((ta) => Number(ta?.team_member_id) === memberId)
      }
      if (Array.isArray(j.job_team_assignments)) {
        return j.job_team_assignments.some((ta) => Number(ta?.team_member_id) === memberId)
      }
      return false
    })
    // Use the SAME completed predicate as the headline KPI — explicitly
    // completed/paid, OR a past-dated job that isn't cancelled/pending —
    // otherwise worker completion % reads lower than the dashboard total.
    const memberCompleted = memberJobs.filter((j) => {
      const s = (j.status || "").toLowerCase()
      if (s === "completed" || s === "paid") return true
      if (s === "cancelled" || s === "pending") return false
      const dt = parseLocalDate(j.scheduled_date)
      if (!dt) return false
      return dt.getTime() <= nowMs
    })
    const memberRevenue = memberJobs.reduce((s, j) => s + jobPrice(j), 0)
    const sparkData = new Array(SPARK_BUCKETS).fill(0)
    memberJobs.forEach((j) => {
      const ds = j.scheduled_date ? String(j.scheduled_date).split(" ")[0] : null
      if (!ds) return
      const idx = bucketIndexFor(ds)
      if (idx >= 0) sparkData[idx] += 1
    })
    // Per-worker utilization — prefer actual hours_worked, otherwise fall
    // back to service_duration so the panel shows something when no one
    // logs hours (most tenants). Denominator is 40h × weeks in the period.
    const hoursFromActual = memberJobs.reduce((s, j) => s + (Number(j.hours_worked) || 0), 0)
    const hoursFromDuration = memberJobs.reduce((s, j) => s + (jobDurationMin(j) / 60), 0)
    const memberHoursWorked = hoursFromActual > 0 ? hoursFromActual : hoursFromDuration
    const memberCapacityHours = 40 * weeksInPeriod
    const memberUtilizationPct = memberCapacityHours > 0
      ? Math.min(100, Math.round((memberHoursWorked / memberCapacityHours) * 100))
      : 0
    return {
      id: m.id,
      name: `${m.first_name || ""} ${m.last_name || ""}`.trim() || m.email || "Unknown",
      subrole: m.role || m.title || "Worker",
      color: m.color || T.blue,
      totalJobs: memberJobs.length,
      completedJobs: memberCompleted.length,
      totalRevenue: memberRevenue,
      avgJobValue: memberCompleted.length ? memberRevenue / memberCompleted.length : 0,
      sparkData,
      hoursWorked: memberHoursWorked,
      hoursWorkedIsEstimated: hoursFromActual === 0 && hoursFromDuration > 0,
      utilizationPct: memberUtilizationPct,
    }
  }).sort((a, b) => b.totalRevenue - a.totalRevenue)

  // ── Top customers (by revenue this period). Revenue, job count and avg
  // ticket all come from the same in-period, non-cancelled jobs — it used
  // to show lifetime invoice spend next to an in-period job count.
  const customerNames = {}
  d.customers.forEach((c) => { customerNames[c.id] = c.first_name || c.name ? `${c.first_name || ""} ${c.last_name || ""}`.trim() || c.name : (c.email || "Customer") })
  const periodRevenueByCustomer = {}
  const periodJobsByCustomer = {}
  billableJobs.forEach((j) => {
    if (!j.customer_id) return
    periodRevenueByCustomer[j.customer_id] = (periodRevenueByCustomer[j.customer_id] || 0) + jobPrice(j)
    periodJobsByCustomer[j.customer_id] = (periodJobsByCustomer[j.customer_id] || 0) + 1
  })
  const topCustomers = Object.entries(periodRevenueByCustomer)
    .filter(([, rev]) => rev > 0)
    .map(([cid, rev]) => {
      const cidNum = Number(cid)
      const sparkData = new Array(SPARK_BUCKETS).fill(0)
      billableJobs.forEach((j) => {
        if (Number(j.customer_id) !== cidNum) return
        const ds = j.scheduled_date ? String(j.scheduled_date).split(" ")[0] : null
        if (!ds) return
        const idx = bucketIndexFor(ds)
        if (idx >= 0) sparkData[idx] += jobPrice(j)
      })
      return {
        id: cid,
        name: customerNames[cid] || `Customer #${cid}`,
        revenue: rev,
        jobs: periodJobsByCustomer[cid] || 0,
        allTimeJobs: jobsByCustomerAllTime[cid] || periodJobsByCustomer[cid] || 0,
        avgTicket: periodJobsByCustomer[cid] ? rev / periodJobsByCustomer[cid] : 0,
        sparkData,
      }
    })
    .sort((a, b) => b.revenue - a.revenue)
    .slice(0, 10)

  // ── 12-week stacked mix (split by service category bucket)
  const weeks = 12
  const weekStart = new Date(d.end)
  weekStart.setDate(weekStart.getDate() - (weeks * 7) + 1)
  const weekBuckets = Array.from({ length: weeks }, (_, w) => {
    const start = new Date(weekStart); start.setDate(start.getDate() + w * 7)
    const end = new Date(start); end.setDate(end.getDate() + 6)
    return { start, end, recurring: 0, oneTime: 0, commercial: 0, addons: 0 }
  })
  // 12 weeks needs more than a 7/30-day window — use the job history.
  const allJobsForWeeks = (d.historyJobs || d.jobs).filter((j) => !isCancelledJob(j))
  allJobsForWeeks.forEach((j) => {
    const dt = parseLocalDate(j.scheduled_date)
    if (!dt) return
    const idx = weekBuckets.findIndex((b) => dt >= b.start && dt <= b.end)
    if (idx === -1) return
    const v = jobPrice(j)
    const svc = (j.service_name || "").toLowerCase()
    const isRecurring = !!j.is_recurring || svc.includes("recurring") || svc.includes("weekly") || svc.includes("biweekly")
    const isCommercial = svc.includes("commercial") || svc.includes("office")
    const isAddon = svc.includes("add") || svc.includes("extra")
    if (isAddon) weekBuckets[idx].addons += v
    else if (isCommercial) weekBuckets[idx].commercial += v
    else if (isRecurring) weekBuckets[idx].recurring += v
    else weekBuckets[idx].oneTime += v
  })

  // ── Reactivated customers: had a job this period AND their previous job
  // (before the period started) was 90+ days earlier. Uses the ~13-month
  // job history; the backend doesn't provide this number.
  const reactivatedCount = (() => {
    const periodStartDay = fmtDateLocal(d.start)
    const lastBefore = {}
    ;(d.historyJobs || []).forEach((j) => {
      if (!j.customer_id || isCancelledJob(j)) return
      const day = String(j.scheduled_date || "").slice(0, 10)
      if (!day || day >= periodStartDay) return
      if (!lastBefore[j.customer_id] || day > lastBefore[j.customer_id]) lastBefore[j.customer_id] = day
    })
    let count = 0
    const seen = new Set()
    billableJobs.forEach((j) => {
      if (!j.customer_id || seen.has(j.customer_id)) return
      seen.add(j.customer_id)
      const prev = parseLocalDate(lastBefore[j.customer_id])
      if (prev && (d.start - prev) / (24 * 60 * 60 * 1000) >= 90) count += 1
    })
    return count
  })()

  // ── Utilization estimate — total job-hours / (workers * 40 * weeksInPeriod)
  // periodDays and weeksInPeriod were hoisted above (shared with team sparklines).
  const totalJobHours = billableJobs.reduce((s, j) => s + (jobDurationMin(j) / 60), 0)
  const workerCount = (d.teamMembers || []).filter((m) => {
    const r = (m.role || "").toLowerCase()
    return /worker|technician/.test(r) || m.is_service_provider
  }).length || 1
  const availableHours = workerCount * 40 * weeksInPeriod
  const utilizationPct = availableHours > 0 ? Math.min(100, Math.round((totalJobHours / availableHours) * 100)) : 0

  // ── Reviews / quality: already-rated jobs feed the funnel "Reviewed" stage;
  // low-rating (<=3 stars) jobs feed the Quality issues card.
  const reviewedCount = ratedJobs.length
  const qualityIssueJobs = d.jobs
    .map((j) => {
      const r = parseFloat(j.customer_rating ?? j.rating)
      if (!(r > 0) || r > 3) return null
      return { id: j.id, customer: j.customer_name || j.customer || `Customer #${j.customer_id || ""}`, rating: r, when: j.scheduled_date }
    })
    .filter(Boolean)
    .sort((a, b) => a.rating - b.rating)
  const qualityIssuesCount = qualityIssueJobs.length

  // ── MRR — compute from the real recurring-job signal (jobs.is_recurring or a
  // populated recurring_frequency). Each recurring job's price is normalized to
  // monthly based on cadence; otherwise averaged over the period. Falls back to
  // the service-name heuristic only when the structured signal is absent.
  // Every visit of a recurring series is its own job row, so multiplying
  // each visit by its cadence (the old way) counted a weekly client ~4×4.3.
  // MRR = recurring revenue actually scheduled in the period, scaled to 30 days.
  const recurringJobsStructured = billableJobs.filter((j) => j.is_recurring || j.recurring_frequency)
  const monthlyRecurringRevenue = recurringJobsStructured.length
    ? recurringJobsStructured.reduce((s, j) => s + jobPrice(j), 0) * (30 / periodDays)
    : null

  // SPARK_BUCKETS and bucketIndexFor were hoisted above (shared with team + customer sparklines).

  // ── On-time arrival — a job counts as "on time" when the recorded
  // start_time lands within +/- 15 minutes of its scheduled_time slot.
  // Only jobs that both got a scheduled_time AND an actual start_time
  // qualify as sample; the rest are ignored so the ratio stays honest.
  const ONTIME_WINDOW_MIN = 15
  const parseScheduledSlot = (j) => {
    const dateStr = j.scheduled_date ? String(j.scheduled_date).split(" ")[0] : null
    const timeStr = j.scheduled_time ? String(j.scheduled_time) : null
    if (!dateStr || !timeStr) return null
    const t = new Date(`${dateStr}T${timeStr}`)
    return Number.isNaN(t.getTime()) ? null : t
  }
  const onTimeSample = d.jobs.filter((j) => j.start_time && parseScheduledSlot(j))
  const onTimeCount = onTimeSample.filter((j) => {
    const scheduled = parseScheduledSlot(j)
    const actual = new Date(j.start_time)
    if (Number.isNaN(actual.getTime())) return false
    return Math.abs(actual - scheduled) / 60000 <= ONTIME_WINDOW_MIN
  }).length
  const onTimePct = onTimeSample.length > 0 ? Math.round((onTimeCount / onTimeSample.length) * 100) : null
  const onTimeSampleSize = onTimeSample.length

  // ── Conversion funnel + summary (prefer API; fallback compute)
  const conv = d.conversionData || {}
  const cs = conv.summary || {}
  // Backend: byStage = { "<stage name>": { total, converted, conversionRate } }
  // keyed by the tenant's own stage names — show them largest-first.
  const convFunnel = (() => {
    const stages = conv.byStage || {}
    const rows = Object.entries(stages)
      .map(([name, v]) => ({ label: name, count: typeof v === "number" ? v : (Number(v?.total) || 0) }))
      .filter((r) => r.count > 0)
      .sort((a, b) => b.count - a.count)
    return rows.length ? rows : null
  })()

  return {
    totalRevenue, invoiceRevenue, jobRevenue, cashCollected, outstandingAR, outstandingCount,
    scheduledCount, completedCount, cancelledCount, pendingCount, inProgressCount, confirmedCount,
    avgJobValue, avgDurationMin,
    activeCustomers, newCustomers, repeatRate, avgLTV, inPeriodCustomerCount,
    totalTips, totalIncentives, avgRating, ratedJobsCount: ratedJobs.length,
    refundAmount, refundCount: refundedInPeriod.length,
    tipsCollected, paymentsCount,
    reactivatedCount,
    utilizationPct, totalJobHours, availableHours, workerCount, weeksInPeriod,
    reviewedCount, qualityIssuesCount, qualityIssueJobs,
    mrrFromRecurring: monthlyRecurringRevenue, recurringJobsCount: recurringJobsStructured.length,
    onTimePct, onTimeCount, onTimeSampleSize,
    daily: { values: dailyValues, labels: dailyLabels },
    monthly, monthlySource, weekday, services, territories, sources, cancelList,
    jobsByCustomerAllTime,
    teamRows, topCustomers, weekBuckets,
    conversion: { summary: cs, funnel: convFunnel, bySource: conv.bySource || {}, timeSeries: conv.timeSeries || [] },
    salary: d.salaryData || { timeSeries: [], memberBreakdown: [], summary: {} },
    lost: d.lostData || { summary: {}, timeSeries: [], lostCustomersList: [] },
    adsSpend: d.adsSpendData || { summary: {}, bySource: [], monthly: [] },
    expensesSummary: d.expensesSummaryData || { summary: {}, jobExpensesByType: {}, businessByCategory: {}, businessExpanded: [] },
    businessExpenses: d.businessExpenses || [],
  }
}

// ══════════════════════════════════════════════════════════════════════
// PERIOD CONTROL
// ══════════════════════════════════════════════════════════════════════

const PeriodControl = ({ value, onChange }) => (
  <div style={{
    display: "inline-flex", gap: 0, background: T.panelSoft,
    borderRadius: 8, padding: 3, border: `1px solid ${T.borderS}`,
  }}>
    {Object.entries(PERIODS).map(([k, v]) => (
      <button
        key={k}
        onClick={() => onChange(k)}
        style={{
          padding: "5px 12px", fontSize: 11.5, fontWeight: 600, border: "none",
          background: value === k ? T.panel : "transparent",
          color: value === k ? T.ink : T.ink2,
          borderRadius: 6, cursor: "pointer",
          boxShadow: value === k ? "0 1px 2px rgba(15,23,42,.08)" : "none",
          textTransform: k === "ytd" || k === "all" ? "uppercase" : "none",
          letterSpacing: k === "ytd" || k === "all" ? ".05em" : "normal",
        }}
      >
        {k.toUpperCase().replace("D", "d")}
      </button>
    ))}
  </div>
)

// ══════════════════════════════════════════════════════════════════════
// OVERVIEW TAB
// ══════════════════════════════════════════════════════════════════════

const OverviewTab = ({ m, data }) => {
  if (!m) return null
  return (
    <div style={{ padding: "14px 24px 24px", display: "flex", flexDirection: "column", gap: 14 }}>
      {/* 5-KPI strip */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 12 }}>
        <SfKPI label="Revenue"        value={moneyShort(m.totalRevenue)} sub={`${m.completedCount} completed`} accent={T.green} />
        <SfKPI label="Jobs done"      value={m.completedCount} sub={`${m.scheduledCount} scheduled`} accent={T.blue} />
        <SfKPI label="New customers"  value={m.newCustomers}   sub={`${m.activeCustomers} active total`} accent={T.purple} />
        <SfKPI label="Avg job value"  value={money(m.avgJobValue)} sub="completed only" accent={T.amber} />
        <SfKPI label="Repeat rate"    value={`${m.repeatRate}%`}   sub="multi-job customers" accent={T.teal} />
      </div>

      {/* 2-col 2-row chart grid */}
      <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 14 }}>
        <SfCard>
          <SfCardHeader
            title="Revenue trend"
            subtitle={`Daily revenue · ${m.daily.values.length} days`}
            right={
              <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
                <span style={{ fontSize: 22, fontWeight: 700, color: T.ink, fontVariantNumeric: "tabular-nums" }}>
                  {moneyShort(m.totalRevenue)}
                </span>
              </div>
            }
          />
          <div style={{ paddingTop: 8, paddingBottom: 24 }}>
            {m.daily.values.some((v) => v > 0) ? (
              <LineAreaChart data={m.daily.values} labels={m.daily.labels} height={220} color={T.blue} gradId="ov-rev" valueFmt={moneyShort} />
            ) : (
              <EmptyChart title="No revenue in this period" subtitle="Try expanding the date range" height={220} />
            )}
          </div>
        </SfCard>

        <SfCard>
          <SfCardHeader title="Revenue by service" subtitle={`Top ${Math.min(5, m.services.length)} by volume`} />
          {m.services.length ? (() => {
            // One unit for the whole donut, never per-slice: use revenue
            // when ANY slice has revenue, otherwise fall back to job count.
            // Mixed-unit slices were producing visible slices labeled "$0".
            const useRev = m.services.slice(0, 5).some((s) => s.revenue > 0)
            const unitOf = (s) => useRev ? s.revenue : s.count
            const sum = m.services.slice(0, 5).reduce((a, b) => a + unitOf(b), 0) || 1
            return (
            <div style={{ display: "flex", alignItems: "center", gap: 18 }}>
              <DonutChart
                data={m.services.slice(0, 5).map((s, i) => ({
                  v: Math.round(unitOf(s)),
                  c: [T.green, T.blue, T.purple, T.amber, T.teal][i],
                }))}
                size={120}
                label={useRev ? "Revenue" : "Jobs"}
                valueFmt={useRev ? moneyShort : ((v) => v.toLocaleString())}
              />
              <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 7 }}>
                {m.services.slice(0, 5).map((s, i) => {
                  const v = unitOf(s)
                  const pct = Math.round((v / sum) * 100)
                  return (
                    <div key={s.name} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12 }}>
                      <span style={{ width: 8, height: 8, borderRadius: 2, background: [T.green, T.blue, T.purple, T.amber, T.teal][i], flexShrink: 0 }} />
                      <span style={{ flex: 1, color: T.ink2, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {s.name}
                      </span>
                      <span style={{ color: T.ink, fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>
                        {useRev ? moneyShort(s.revenue) : s.count}
                      </span>
                      <span style={{ color: T.ink3, fontWeight: 600, fontVariantNumeric: "tabular-nums", width: 36, textAlign: "right" }}>
                        {pct}%
                      </span>
                    </div>
                  )
                })}
              </div>
            </div>
            )
          })() : (
            <EmptyChart title="No service data" />
          )}
        </SfCard>

        <SfCard>
          <SfCardHeader title="Jobs completed" subtitle="By weekday" />
          <div style={{ paddingTop: 8 }}>
            {m.weekday.some((v) => v > 0) ? (
              <BarChart data={m.weekday} labels={["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"]} color={T.blue} height={180} />
            ) : <EmptyChart title="No scheduled jobs" />}
          </div>
        </SfCard>

        <SfCard>
          <SfCardHeader title="Lead sources" subtitle="Where customers come from" />
          {m.sources.length ? (
            <HBarList
              data={m.sources.map((s, i) => ({ ...s, c: [T.green, T.blue, T.red, T.purple, T.amber, T.ink3][i] || T.ink3 }))}
              fmt={(v) => v}
            />
          ) : <EmptyChart title="No source data" />}
        </SfCard>

        {/* Team performance footer */}
        <SfCard style={{ gridColumn: "1 / -1" }} padding={false}>
          <div style={{ padding: "14px 18px", borderBottom: `1px solid ${T.borderS}`, display: "flex", alignItems: "center" }}>
            <div>
              <div style={{ fontSize: 13.5, fontWeight: 600, color: T.ink }}>Team performance</div>
              <div style={{ fontSize: 11.5, color: T.ink3, marginTop: 1 }}>
                {m.teamRows.length} members · sorted by revenue
              </div>
            </div>
            <div style={{ flex: 1 }} />
          </div>
          {m.teamRows.length ? (
            <div>
              {m.teamRows.slice(0, 8).map((t, i) => (
                <div key={t.id} style={{
                  display: "flex", alignItems: "center", gap: 14, padding: "11px 18px",
                  borderBottom: i < Math.min(m.teamRows.length, 8) - 1 ? `1px solid ${T.borderS}` : "none",
                }}>
                  <div style={{ width: 24, fontSize: 11.5, color: T.ink3, fontWeight: 700, fontFamily: "var(--sf-font-mono, monospace)" }}>#{i + 1}</div>
                  <SfAvatar initials={sfInitials(t.name)} color={t.color} size={28} />
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: T.ink }}>{t.name}</div>
                    <div style={{ fontSize: 11, color: T.ink3, marginTop: 1 }}>{t.subrole}</div>
                  </div>
                  <div style={{ width: 80, textAlign: "right" }}>
                    <div style={{ fontSize: 10.5, color: T.ink3, fontWeight: 600, textTransform: "uppercase", letterSpacing: ".04em" }}>Jobs</div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: T.ink, fontVariantNumeric: "tabular-nums" }}>{t.totalJobs}</div>
                  </div>
                  <div style={{ width: 120, textAlign: "right" }}>
                    <div style={{ fontSize: 10.5, color: T.ink3, fontWeight: 600, textTransform: "uppercase", letterSpacing: ".04em" }}>Revenue</div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: T.ink, fontVariantNumeric: "tabular-nums" }}>{money(t.totalRevenue)}</div>
                  </div>
                  <div style={{ width: 100, textAlign: "right" }}>
                    <div style={{ fontSize: 10.5, color: T.ink3, fontWeight: 600, textTransform: "uppercase", letterSpacing: ".04em" }}>Completion</div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: T.ink, fontVariantNumeric: "tabular-nums" }}>
                      {pct(t.completedJobs, t.totalJobs)}%
                    </div>
                  </div>
                  <div style={{ width: 90 }}>
                    {t.sparkData?.some((n) => n > 0)
                      ? <MiniSpark data={t.sparkData} color={t.color} />
                      : <div style={{ fontSize: 10, color: T.ink3, textAlign: "center" }}>no jobs</div>}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <EmptyChart title="No team data" />
          )}
        </SfCard>
      </div>
    </div>
  )
}

// ══════════════════════════════════════════════════════════════════════
// REVENUE TAB
// ══════════════════════════════════════════════════════════════════════

const RevenueTab = ({ m }) => {
  if (!m) return null
  // Revenue per customer who actually had work this period.
  const arpc = m.inPeriodCustomerCount > 0 ? m.totalRevenue / m.inPeriodCustomerCount : 0
  // MRR priority:
  //   1. Recurring-job revenue in the period, scaled to 30 days
  //   2. Service-name heuristic fallback for tenants that haven't flipped the flag
  const periodDaysForMrr = Math.max(1, (m.weeksInPeriod || 1) * 7)
  const recurringRev = m.services
    .filter((s) => /recurring|weekly|biweekly/i.test(s.name))
    .reduce((s, x) => s + x.revenue, 0) * (30 / periodDaysForMrr)
  const mrr = m.mrrFromRecurring != null ? m.mrrFromRecurring : recurringRev
  const mrrSource = m.mrrFromRecurring != null
    ? `per month · ${m.recurringJobsCount} recurring visit${m.recurringJobsCount === 1 ? "" : "s"}`
    : "per month · service-name heuristic"

  const monthlyRev = m.monthly.map((r) => Math.round(r.revenue))
  const monthlyLabels = m.monthly.map((r) => r.label)
  const bestMonth = m.monthly.reduce((best, r) => (r.revenue > (best?.revenue || 0) ? r : best), null)

  return (
    <div style={{ padding: "14px 24px 24px", display: "flex", flexDirection: "column", gap: 14 }}>
      {/* 6-KPI strip */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gap: 12 }}>
        <SfKPI label="Total revenue"  value={moneyShort(m.totalRevenue)} sub="this period"        accent={T.green} />
        <SfKPI label="MRR · recurring" value={moneyShort(mrr)} sub={mrr > 0 ? mrrSource : "no recurring revenue"} accent={T.blue} />
        <SfKPI label="ARPC"           value={money(arpc)} sub="avg per customer"                  accent={T.purple} />
        <SfKPI label="Cash collected" value={moneyShort(m.cashCollected)} sub={m.paymentsCount != null ? `${m.paymentsCount} payment${m.paymentsCount === 1 ? "" : "s"} · ${pct(m.cashCollected, m.totalRevenue)}% of revenue` : `${pct(m.cashCollected, m.totalRevenue)}% collection`} accent={T.teal} />
        <SfKPI label="Outstanding AR" value={moneyShort(m.outstandingAR)} sub={`${m.outstandingCount} open invoice${m.outstandingCount === 1 ? "" : "s"} · all-time`} accent={T.amber} />
        {/* No refund status exists in the data, so the old Refunds card was
            always $0. Tips actually collected is real data. */}
        <SfKPI label="Tips collected" value={m.tipsCollected != null ? moneyShort(m.tipsCollected) : moneyShort(m.totalTips)} sub={m.tipsCollected != null ? "from recorded payments" : "from job tips"} accent={T.red} />
      </div>

      {/* Daily revenue hero */}
      <SfCard>
        <div style={{ display: "flex", alignItems: "flex-end", marginBottom: 12 }}>
          <div>
            <div style={{ fontSize: 13.5, fontWeight: 700, color: T.ink, letterSpacing: "-0.005em" }}>
              Daily revenue · {m.daily.values.length} days
            </div>
            <div style={{ fontSize: 11.5, color: T.ink3, marginTop: 2 }}>
              {m.invoiceRevenue > m.jobRevenue ? "From invoices" : "From job pricing"}
            </div>
          </div>
          <div style={{ flex: 1 }} />
          <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
            <span style={{ fontSize: 26, fontWeight: 700, color: T.ink, letterSpacing: "-0.02em", fontVariantNumeric: "tabular-nums" }}>
              {moneyShort(m.totalRevenue)}
            </span>
          </div>
        </div>
        <div style={{ paddingTop: 8, paddingBottom: 22 }}>
          {m.daily.values.some((v) => v > 0) ? (
            <LineAreaChart data={m.daily.values} labels={m.daily.labels} height={200} color={T.blue} gradId="rev-tab-grad" valueFmt={moneyShort} />
          ) : (
            <EmptyChart title="No revenue in this period" height={200} />
          )}
        </div>
      </SfCard>

      {/* 2-col */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <SfCard>
          <SfCardHeader title="Revenue by service" subtitle={`${m.services.length} services · ${m.scheduledCount} jobs`} />
          {m.services.length ? (() => {
            // Same mixed-unit fix as Overview: one unit for the whole donut.
            const useRev = m.services.slice(0, 5).some((s) => s.revenue > 0)
            const unitOf = (s) => useRev ? s.revenue : s.count
            const denom = m.services.slice(0, 5).reduce((a, x) => a + unitOf(x), 0) || 1
            return (
            <div style={{ display: "flex", alignItems: "center", gap: 24 }}>
              <DonutChart
                data={m.services.slice(0, 5).map((s, i) => ({
                  v: Math.round(unitOf(s)),
                  c: [T.green, T.blue, T.purple, T.amber, T.teal][i],
                }))}
                size={140}
                label={useRev ? "Revenue" : "Jobs"}
                valueFmt={useRev ? moneyShort : ((v) => v.toLocaleString())}
              />
              <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 9 }}>
                {m.services.slice(0, 5).map((s, i) => {
                  const p = Math.round((unitOf(s) / denom) * 100)
                  return (
                    <div key={s.name} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12 }}>
                      <span style={{ width: 8, height: 8, borderRadius: 2, background: [T.green, T.blue, T.purple, T.amber, T.teal][i], flexShrink: 0 }} />
                      <span style={{ flex: 1, color: T.ink2, fontWeight: 500 }}>{s.name}</span>
                      <span style={{ color: T.ink, fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>
                        {useRev ? money(s.revenue) : `${s.count} job${s.count === 1 ? "" : "s"}`}
                      </span>
                      <span style={{ color: T.ink3, fontWeight: 600, fontVariantNumeric: "tabular-nums", minWidth: 32, textAlign: "right" }}>{p}%</span>
                    </div>
                  )
                })}
              </div>
            </div>
            )
          })() : (
            <EmptyChart title="No service data" />
          )}
        </SfCard>

        <SfCard>
          <SfCardHeader title="Monthly trend" subtitle={`Last 7 months · ${m.monthlySource || "invoice-based"}`} />
          <div style={{ paddingTop: 8 }}>
            {monthlyRev.some((v) => v > 0) ? (
              <BarChart data={monthlyRev} labels={monthlyLabels} color={T.green} height={170} valueFmt={(v) => moneyShort(v)} />
            ) : (
              <EmptyChart title="No revenue history" height={170} />
            )}
          </div>
          <div style={{ display: "flex", gap: 10, marginTop: 16, paddingTop: 12, borderTop: `1px solid ${T.borderS}` }}>
            <Mini label="Best month" value={bestMonth?.label || "—"} sub={bestMonth ? moneyShort(bestMonth.revenue) : ""} />
            <Mini label="Avg / mo" value={moneyShort(monthlyRev.reduce((a, b) => a + b, 0) / Math.max(1, monthlyRev.filter((v) => v > 0).length))} sub="last 7 mo" />
            {(() => {
              // Compare the trailing 3-month average with the leading 3 so a
              // single zero month at either end doesn't flip the arrow.
              const first3 = monthlyRev.slice(0, 3).reduce((a, b) => a + b, 0) / 3
              const last3 = monthlyRev.slice(-3).reduce((a, b) => a + b, 0) / 3
              const arrow = last3 > first3 * 1.05 ? "↑" : last3 < first3 * 0.95 ? "↓" : "→"
              const deltaPct = first3 > 0 ? Math.round(((last3 - first3) / first3) * 100) : null
              return <Mini label="Trend" value={arrow} sub={deltaPct == null ? "vs first 3 mo" : `${deltaPct > 0 ? "+" : ""}${deltaPct}% vs first 3 mo`} />
            })()}
          </div>
        </SfCard>
      </div>

      {/* 12-week stacked mix */}
      <SfCard>
        <SfCardHeader
          title="Revenue mix · 12 weeks"
          subtitle="Recurring · One-time · Commercial · Add-ons"
          right={
            <div style={{ display: "flex", gap: 12 }}>
              {[
                { l: "Recurring", c: T.green },
                { l: "One-time", c: T.blue },
                { l: "Commercial", c: T.purple },
                { l: "Add-ons", c: T.amber },
              ].map((s) => (
                <span key={s.l} style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11, color: T.ink2 }}>
                  <span style={{ width: 10, height: 10, borderRadius: 2, background: s.c }} />
                  {s.l}
                </span>
              ))}
            </div>
          }
        />
        {(() => {
          const totals = m.weekBuckets.map((b) => b.recurring + b.oneTime + b.commercial + b.addons)
          const maxWk = Math.max(1, ...totals)
          if (maxWk < 1) return <EmptyChart title="Not enough job history" height={180} />
          // Fixed pixel height for the stacking area — percent-height children
          // inside a flex column collapse to 0 unless the parent has a definite
          // height, which was why W8–W12 showed totals but no colored bars.
          const BAR_AREA_PX = 150
          return (
            <div style={{ display: "flex", alignItems: "flex-end", gap: 8, paddingTop: 6 }}>
              {m.weekBuckets.map((b, w) => {
                const total = totals[w]
                const segs = [
                  { v: b.recurring, c: T.green },
                  { v: b.oneTime, c: T.blue },
                  { v: b.commercial, c: T.purple },
                  { v: b.addons, c: T.amber },
                ]
                return (
                  <div key={w} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
                    <div style={{
                      height: BAR_AREA_PX, width: "100%",
                      display: "flex", flexDirection: "column-reverse",
                      borderRadius: "4px 4px 0 0", overflow: "hidden",
                      background: total > 0 ? "transparent" : T.panelAlt,
                    }}>
                      {total > 0 && segs.filter((s) => s.v > 0).map((s, i) => {
                        const segH = Math.max(2, (s.v / maxWk) * BAR_AREA_PX)
                        return (
                          <div
                            key={i}
                            title={moneyShort(s.v)}
                            style={{
                              width: "100%",
                              height: `${segH}px`,
                              background: s.c,
                              borderBottom: i > 0 ? `1px solid ${T.panel}` : "none",
                              display: "flex", alignItems: "center", justifyContent: "center",
                            }}
                          >
                            {segH >= 14 && (
                              <span style={{
                                fontSize: 9, color: "#fff", fontWeight: 700,
                                fontVariantNumeric: "tabular-nums", letterSpacing: "-0.01em",
                                textShadow: "0 1px 1px rgba(0,0,0,.25)",
                              }}>
                                {moneyShort(s.v)}
                              </span>
                            )}
                          </div>
                        )
                      })}
                    </div>
                    <div style={{ fontSize: 9.5, color: T.ink3, fontWeight: 600 }}>W{w + 1}</div>
                    <div style={{ fontSize: 10.5, color: T.ink, fontWeight: 700, fontVariantNumeric: "tabular-nums", marginTop: -2 }}>
                      {total > 0 ? moneyShort(total) : "—"}
                    </div>
                  </div>
                )
              })}
            </div>
          )
        })()}
      </SfCard>

      {/* Top customers */}
      <SfCard padding={false}>
        <div style={{ padding: "12px 18px", borderBottom: `1px solid ${T.borderS}`, display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ width: 28, height: 28, borderRadius: 7, background: T.greenSoft, color: T.greenDark, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <DollarSign size={14} strokeWidth={2.1} />
          </div>
          <div>
            <div style={{ fontSize: 13.5, fontWeight: 700, color: T.ink }}>Top customers by revenue</div>
            <div style={{ fontSize: 11, color: T.ink3, marginTop: 1 }}>This period · {m.topCustomers.length} accounts</div>
          </div>
          <div style={{ flex: 1 }} />
          <SfButton variant="ghost" size="sm" icon={RefreshCw}>Sort: Revenue ↓</SfButton>
        </div>
        <div style={{
          display: "grid", gridTemplateColumns: "40px 2fr 120px 100px 100px 110px", gap: 14,
          padding: "10px 18px", background: T.panelAlt, borderBottom: `1px solid ${T.borderS}`,
          fontSize: 10.5, color: T.ink3, fontWeight: 700, letterSpacing: ".05em", textTransform: "uppercase",
        }}>
          <div>#</div>
          <div>Customer</div>
          <div style={{ textAlign: "right" }}>Total spend</div>
          <div style={{ textAlign: "right" }}>Jobs</div>
          <div style={{ textAlign: "right" }}>Avg ticket</div>
          <div style={{ textAlign: "right" }}>Trend</div>
        </div>
        {m.topCustomers.length ? m.topCustomers.map((c, i, arr) => (
          <div key={c.id} style={{
            display: "grid", gridTemplateColumns: "40px 2fr 120px 100px 100px 110px", gap: 14,
            padding: "11px 18px", alignItems: "center",
            borderBottom: i < arr.length - 1 ? `1px solid ${T.borderS}` : "none",
          }}>
            <div style={{ fontSize: 12, color: T.ink3, fontWeight: 700, fontFamily: "var(--sf-font-mono, monospace)" }}>#{i + 1}</div>
            <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
              <SfAvatar initials={sfInitials(c.name)} color={[T.purple, T.green, T.blue, T.amber][i % 4]} size={26} />
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 12.5, fontWeight: 600, color: T.ink }}>{c.name}</div>
                <div style={{ fontSize: 10.5, color: T.ink3, marginTop: 1 }}>
                  {(c.allTimeJobs || c.jobs) > 1 ? "Returning" : "One-time"}
                </div>
              </div>
            </div>
            <div style={{ textAlign: "right", fontSize: 14, fontWeight: 700, color: T.ink, fontVariantNumeric: "tabular-nums", letterSpacing: "-0.01em" }}>
              {money(c.revenue)}
            </div>
            <div style={{ textAlign: "right", fontSize: 12.5, color: T.ink2, fontVariantNumeric: "tabular-nums" }}>{c.jobs}</div>
            <div style={{ textAlign: "right", fontSize: 12.5, color: T.ink2, fontVariantNumeric: "tabular-nums" }}>{money(c.avgTicket)}</div>
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              {c.sparkData?.some((n) => n > 0)
                ? <MiniSpark data={c.sparkData} color={T.green} />
                : <div style={{ fontSize: 10, color: T.ink3 }}>—</div>}
            </div>
          </div>
        )) : (
          <EmptyChart title="No customer revenue in this period" />
        )}
      </SfCard>
    </div>
  )
}

// ══════════════════════════════════════════════════════════════════════
// JOBS TAB
// ══════════════════════════════════════════════════════════════════════

const JobsTab = ({ m }) => {
  if (!m) return null

  // Funnel — map our statuses to design's lifecycle stages
  const funnel = [
    { label: "Scheduled",   count: m.scheduledCount,   color: T.ink2 },
    { label: "Confirmed",   count: m.confirmedCount + m.inProgressCount + m.completedCount, color: T.blue },
    { label: "In progress", count: m.inProgressCount + m.completedCount, color: T.amber },
    { label: "Completed",   count: m.completedCount,   color: T.greenDark },
    { label: "Reviewed",    count: m.reviewedCount || 0, color: T.purple },
  ]
  const funnelMax = Math.max(1, ...funnel.map((s) => s.count))
  const completionPct = m.scheduledCount > 0 ? (m.completedCount / m.scheduledCount) * 100 : 0

  return (
    <div style={{ padding: "14px 24px 24px", display: "flex", flexDirection: "column", gap: 14 }}>
      {/* 6 KPIs */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gap: 12 }}>
        <SfKPI label="Jobs scheduled" value={m.scheduledCount} sub="all statuses" accent={T.blue} />
        <SfKPI label="Completed"      value={m.completedCount} sub={`${pct(m.completedCount, m.scheduledCount)}% completion`} accent={T.green} />
        <SfKPI label="Cancelled"      value={m.cancelledCount} sub={`${pct(m.cancelledCount, m.scheduledCount)}% rate`} accent={T.red} />
        <SfKPI label="Pending"        value={m.pendingCount}   sub="awaiting confirm" accent={T.amber} />
        <SfKPI label="In progress"    value={m.inProgressCount} sub="active now" accent={T.purple} />
        <SfKPI label="Avg duration"   value={m.avgDurationMin ? `${(m.avgDurationMin / 60).toFixed(1)}h` : "—"} sub="from service_duration" accent={T.teal} />
      </div>

      {/* Funnel */}
      <SfCard padding={false}>
        <div style={{ padding: "12px 18px", borderBottom: `1px solid ${T.borderS}`, display: "flex", alignItems: "center", gap: 10 }}>
          <Workflow size={14} color={T.ink2} />
          <div>
            <div style={{ fontSize: 13.5, fontWeight: 700, color: T.ink }}>Job lifecycle funnel</div>
            <div style={{ fontSize: 11, color: T.ink3, marginTop: 1 }}>Stage counts · this period</div>
          </div>
          <div style={{ flex: 1 }} />
          <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
            <span style={{ fontSize: 11.5, color: T.ink3, fontWeight: 600, letterSpacing: ".04em", textTransform: "uppercase" }}>
              Completion
            </span>
            <span style={{ fontSize: 18, fontWeight: 700, color: T.greenDark, fontVariantNumeric: "tabular-nums" }}>
              {completionPct.toFixed(1)}%
            </span>
          </div>
        </div>
        <div style={{ padding: "18px 24px", display: "flex", flexDirection: "column", gap: 10 }}>
          {funnel.map((s, i) => {
            const stagePct = (s.count / funnelMax) * 100
            const drop = i > 0 ? funnel[i - 1].count - s.count : 0
            const dropPct = i > 0 && funnel[i - 1].count > 0 ? Math.round((drop / funnel[i - 1].count) * 100) : 0
            return (
              <div key={s.label} style={{ display: "flex", alignItems: "center", gap: 14 }}>
                <div style={{ width: 100, fontSize: 12, fontWeight: 600, color: T.ink }}>{s.label}</div>
                <div style={{ flex: 1, position: "relative", height: 38 }}>
                  <div style={{
                    position: "absolute", left: 0, top: 0, bottom: 0,
                    width: `${Math.max(stagePct, 2)}%`,
                    background: s.color,
                    borderRadius: 6, display: "flex", alignItems: "center", paddingLeft: 14,
                  }}>
                    <span style={{ color: "#fff", fontWeight: 700, fontSize: 14, letterSpacing: "-0.01em", fontVariantNumeric: "tabular-nums" }}>
                      {s.count}
                    </span>
                    <span style={{ color: "rgba(255,255,255,.85)", fontSize: 11.5, fontWeight: 600, marginLeft: 8 }}>
                      {Math.round(stagePct)}%
                    </span>
                  </div>
                </div>
                <div style={{ width: 140, textAlign: "right" }}>
                  {i > 0 && drop > 0 ? (
                    <>
                      <div style={{ fontSize: 12, color: T.redDark, fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>
                        −{drop} <span style={{ color: T.ink3, fontWeight: 500 }}>({dropPct}%)</span>
                      </div>
                      <div style={{ fontSize: 10.5, color: T.ink3, marginTop: 1 }}>drop from {funnel[i - 1].label.toLowerCase()}</div>
                    </>
                  ) : i === 0 ? (
                    <div style={{ fontSize: 10.5, color: T.ink3, fontWeight: 600, letterSpacing: ".04em", textTransform: "uppercase" }}>
                      Starting volume
                    </div>
                  ) : null}
                </div>
              </div>
            )
          })}
        </div>
      </SfCard>

      {/* Type + Territory */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <SfCard>
          <SfCardHeader title="Jobs by type" subtitle={`${m.scheduledCount} jobs in scope`} />
          {m.services.length ? (
            <div style={{ display: "flex", alignItems: "center", gap: 24 }}>
              <DonutChart
                data={m.services.slice(0, 5).map((s, i) => ({ v: s.count, c: [T.green, T.blue, T.purple, T.amber, T.teal][i] }))}
                size={140}
                label="Jobs"
              />
              <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 9 }}>
                {(() => {
                  const denom = m.services.slice(0, 5).reduce((a, x) => a + x.count, 0) || 1
                  return m.services.slice(0, 5).map((s, i) => {
                    const p = Math.round((s.count / denom) * 100)
                    return (
                      <div key={s.name} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12 }}>
                        <span style={{ width: 8, height: 8, borderRadius: 2, background: [T.green, T.blue, T.purple, T.amber, T.teal][i], flexShrink: 0 }} />
                        <span style={{ flex: 1, color: T.ink2, fontWeight: 500 }}>{s.name}</span>
                        <span style={{ color: T.ink, fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>{s.count}</span>
                        <span style={{ color: T.ink3, fontWeight: 600, fontVariantNumeric: "tabular-nums", minWidth: 32, textAlign: "right" }}>{p}%</span>
                      </div>
                    )
                  })
                })()}
              </div>
            </div>
          ) : <EmptyChart title="No jobs by service" />}
        </SfCard>

        <SfCard>
          <SfCardHeader title="Jobs by territory" subtitle="Where the work is" />
          {m.territories.length ? (
            <HBarList data={m.territories.map((r, i) => ({ ...r, c: [T.blue, T.purple, T.green, T.amber, T.red, T.teal][i] || T.ink3 }))} />
          ) : <EmptyChart title="No territory data" />}
        </SfCard>
      </div>

      {/* Weekday + Cancellations */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <SfCard>
          <SfCardHeader title="Jobs by weekday" subtitle="Period-wide totals" />
          <div style={{ paddingTop: 8 }}>
            {m.weekday.some((v) => v > 0) ? (
              <BarChart data={m.weekday} labels={["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"]} color={T.blue} height={180} />
            ) : <EmptyChart title="No scheduled jobs" />}
          </div>
        </SfCard>

        <SfCard>
          <SfCardHeader title="Cancellation reasons" subtitle={`${m.cancelledCount} cancellations`} />
          {m.cancelList.length ? (
            <HBarList data={m.cancelList.map((r, i) => ({ ...r, c: [T.amber, T.red, T.blue, T.purple, T.redDark, T.ink3][i] || T.ink3 }))} />
          ) : <EmptyChart title="No cancellations" subtitle="That's good news" />}
        </SfCard>
      </div>
    </div>
  )
}

// ══════════════════════════════════════════════════════════════════════
// TEAM TAB
// ══════════════════════════════════════════════════════════════════════

const TeamTab = ({ m }) => {
  if (!m) return null
  const workers = m.teamRows
  const avgJobs = workers.length ? workers.reduce((s, w) => s + w.totalJobs, 0) / workers.length : 0
  const avgRev = workers.length ? workers.reduce((s, w) => s + w.totalRevenue, 0) / workers.length : 0

  return (
    <div style={{ padding: "14px 24px 24px", display: "flex", flexDirection: "column", gap: 14 }}>
      {/* 6 KPIs */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gap: 12 }}>
        <SfKPI label="Workers active" value={workers.length} sub="all members" accent={T.blue} />
        <SfKPI label="Avg jobs / worker" value={Math.round(avgJobs)} sub="this period" accent={T.purple} />
        <SfKPI label="Avg rev / worker" value={moneyShort(avgRev)} sub="this period" accent={T.green} />
        <SfKPI
          label="Avg rating"
          value={m.avgRating > 0 ? m.avgRating.toFixed(2) : "—"}
          sub={m.ratedJobsCount > 0 ? `${m.ratedJobsCount} rated job${m.ratedJobsCount === 1 ? "" : "s"}` : "no ratings yet"}
          accent={T.amber}
        />
        <SfKPI
          label="Utilization"
          value={m.utilizationPct > 0 ? `${m.utilizationPct}%` : "—"}
          sub={m.totalJobHours > 0 ? `${m.totalJobHours.toFixed(0)}h / ${Math.round(m.availableHours)}h` : "no job hours logged"}
          accent={T.teal}
        />
        <SfKPI
          label="On-time arrival"
          value={m.onTimePct != null ? `${m.onTimePct}%` : "—"}
          sub={m.onTimeSampleSize > 0
            ? `${m.onTimeCount}/${m.onTimeSampleSize} within ±15 min`
            : "no start times logged"}
          accent={T.green}
        />
      </div>

      {/* Leaderboard */}
      <SfCard padding={false}>
        <div style={{ padding: "12px 18px", background: T.panelAlt, borderBottom: `1px solid ${T.borderS}`, display: "flex", alignItems: "center", gap: 10 }}>
          <Users size={14} color={T.ink2} />
          <div>
            <div style={{ fontSize: 13.5, fontWeight: 700, color: T.ink }}>Worker performance</div>
            <div style={{ fontSize: 11, color: T.ink3, marginTop: 1 }}>Revenue · jobs · completion</div>
          </div>
          <div style={{ flex: 1 }} />
          <SfButton variant="ghost" size="sm" icon={RefreshCw}>Sort: Revenue ↓</SfButton>
        </div>
        <div style={{
          display: "grid", gridTemplateColumns: "40px 1.6fr 110px 90px 110px 130px", gap: 14,
          padding: "10px 18px", background: T.panel, borderBottom: `1px solid ${T.borderS}`,
          fontSize: 10.5, color: T.ink3, fontWeight: 700, letterSpacing: ".05em", textTransform: "uppercase",
        }}>
          <div>#</div>
          <div>Worker</div>
          <div style={{ textAlign: "right" }}>Revenue</div>
          <div style={{ textAlign: "right" }}>Jobs</div>
          <div style={{ textAlign: "right" }}>Completion</div>
          <div style={{ textAlign: "right" }}>Trend</div>
        </div>
        {workers.length ? workers.map((w, i, arr) => (
          <div key={w.id} style={{
            display: "grid", gridTemplateColumns: "40px 1.6fr 110px 90px 110px 130px", gap: 14,
            padding: "12px 18px", alignItems: "center",
            borderBottom: i < arr.length - 1 ? `1px solid ${T.borderS}` : "none",
          }}>
            <div style={{
              width: 28, height: 28, borderRadius: 7,
              background: i === 0 ? `${alpha(T.amber, "22")}` : i === 1 ? `${T.ink3}22` : i === 2 ? "#A87C4F22" : T.panelAlt,
              color:      i === 0 ? T.amberDark : i === 1 ? T.ink2 : i === 2 ? "#A87C4F" : T.ink3,
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 11.5, fontWeight: 700, fontVariantNumeric: "tabular-nums",
            }}>{i + 1}</div>
            <div style={{ display: "flex", alignItems: "center", gap: 11 }}>
              <SfAvatar initials={sfInitials(w.name)} color={w.color} size={32} />
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: T.ink }}>{w.name}</div>
                <div style={{ fontSize: 10.5, color: T.ink3, marginTop: 1 }}>{w.subrole}</div>
              </div>
            </div>
            <div style={{ textAlign: "right", fontSize: 14, fontWeight: 700, color: T.ink, fontVariantNumeric: "tabular-nums", letterSpacing: "-0.01em" }}>
              {money(w.totalRevenue)}
            </div>
            <div style={{ textAlign: "right", fontSize: 13, fontWeight: 600, color: T.ink2, fontVariantNumeric: "tabular-nums" }}>
              {w.totalJobs}
            </div>
            <div style={{ textAlign: "right", fontSize: 13.5, fontWeight: 700, color: pct(w.completedJobs, w.totalJobs) >= 90 ? T.greenDark : T.amberDark, fontVariantNumeric: "tabular-nums" }}>
              {pct(w.completedJobs, w.totalJobs)}%
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              {w.sparkData?.some((n) => n > 0)
                ? <MiniSpark data={w.sparkData} color={w.color} />
                : <div style={{ fontSize: 10, color: T.ink3 }}>—</div>}
            </div>
          </div>
        )) : (
          <EmptyChart title="No team data" />
        )}
      </SfCard>

      {/* Quality + Utilization */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <SfCard>
          <SfCardHeader
            title="Quality issues"
            subtitle={`Customer-reported · ${m.qualityIssuesCount || 0} job${m.qualityIssuesCount === 1 ? "" : "s"} ≤3★`}
          />
          {m.qualityIssuesCount > 0 ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 6, maxHeight: 220, overflowY: "auto" }}>
              {m.qualityIssueJobs.slice(0, 8).map((row) => (
                <div key={row.id} style={{
                  display: "flex", alignItems: "center", gap: 10, padding: "8px 10px",
                  borderRadius: 6, background: T.panelAlt, border: `1px solid ${T.borderS}`,
                }}>
                  <div style={{
                    width: 32, height: 24, borderRadius: 4,
                    background: row.rating <= 2 ? `${alpha(T.red, "20")}` : `${alpha(T.amber, "20")}`,
                    color:      row.rating <= 2 ? T.red : T.amberDark,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 11, fontWeight: 700,
                  }}>{row.rating}★</div>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontSize: 12.5, fontWeight: 600, color: T.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {row.customer}
                    </div>
                    {row.when && (
                      <div style={{ fontSize: 10.5, color: T.ink3, marginTop: 1 }}>{row.when}</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <EmptyChart
              icon={Star}
              title={m.ratedJobsCount > 0 ? "All ratings 4★ or higher this period" : "No reviews imported yet"}
              subtitle={m.ratedJobsCount > 0 ? `${m.ratedJobsCount} rated job${m.ratedJobsCount === 1 ? "" : "s"}` : "Import reviews from BK / ZB to populate"}
            />
          )}
        </SfCard>

        <SfCard>
          <SfCardHeader
            title="Utilization by worker"
            subtitle={workers.some((w) => w.hoursWorked > 0 && w.hoursWorkedIsEstimated)
              ? "Hours ÷ (40h × weeks) · estimated from scheduled duration"
              : "Actual hours worked ÷ (40h × weeks)"}
          />
          {workers.filter((w) => w.hoursWorked > 0).length ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 8, maxHeight: 220, overflowY: "auto" }}>
              {workers.filter((w) => w.hoursWorked > 0).slice(0, 10).map((w) => (
                <div key={w.id} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div style={{ width: 110, fontSize: 12, color: T.ink, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {w.name}
                  </div>
                  <div style={{ flex: 1, height: 8, background: T.panelAlt, borderRadius: 4, overflow: "hidden" }}>
                    <div style={{
                      width: `${Math.min(100, w.utilizationPct)}%`, height: "100%",
                      background: w.utilizationPct >= 70 ? T.green : w.utilizationPct >= 40 ? T.amber : T.red,
                    }} />
                  </div>
                  <div style={{ width: 90, textAlign: "right", fontSize: 11.5, color: T.ink2, fontVariantNumeric: "tabular-nums" }}>
                    {w.hoursWorked.toFixed(1)}h · {w.utilizationPct}%
                    {w.hoursWorkedIsEstimated && <span title="Estimated from service_duration" style={{ color: T.ink3, fontWeight: 600 }}> *</span>}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <EmptyChart
              icon={TrendingUp}
              title="No scheduled hours this period"
              subtitle="Fills in once jobs have a service_duration or logged hours"
            />
          )}
        </SfCard>
      </div>
    </div>
  )
}

// ══════════════════════════════════════════════════════════════════════
// CUSTOMERS TAB
// ══════════════════════════════════════════════════════════════════════

const cohortColor = (v) => {
  if (v === null || v === undefined) return T.panelSoft
  if (v >= 0.85) return T.greenDark
  if (v >= 0.75) return "#52BC7C"
  if (v >= 0.65) return "#86D3A2"
  if (v >= 0.50) return "#BBE5C8"
  return "#F0E0D0"
}

const CustomersTab = ({ m, data }) => {
  if (!m) return null
  // Backend (lost-customers): summary.lostCustomers = customers whose last
  // job was 90+ days ago; summary.churnRate = lost / (active + lost) * 100.
  const lostSummary = m.lost?.summary || {}
  const lost = Number(lostSummary.lostCustomers ?? lostSummary.total ?? m.lost?.lostCustomersList?.length ?? 0) || 0
  const churnRate = lostSummary.churnRate != null
    ? Number(lostSummary.churnRate).toFixed(1)
    : (m.activeCustomers > 0 ? ((lost / m.activeCustomers) * 100).toFixed(1) : "0.0")
  // Recurring share — prefer the customer-level flag; fall back to deriving
  // it from jobs (any customer with an is_recurring/recurring_frequency job
  // counts as recurring). Most tenants only flip the flag on jobs, so the
  // customer-only read used to show 0% even when recurring work existed.
  const recurringShare = (() => {
    const custList = data?.customers || []
    if (!custList.length) return 0
    const flagged = new Set()
    custList.forEach((c) => {
      if ((c.customer_type || "").toLowerCase().includes("recurring") || c.is_recurring) flagged.add(c.id)
    })
    ;(data?.jobs || []).forEach((j) => {
      if (!j.customer_id) return
      if (j.is_recurring || j.recurring_frequency) flagged.add(j.customer_id)
    })
    return Math.round((flagged.size / custList.length) * 100)
  })()

  // ── Build cohort matrix from customer creation dates + their job activity
  // Cohorts = last 6 months; M0..M5 = months since cohort
  const cohorts = (() => {
    const now = new Date()
    const cohortMonths = []
    for (let i = 5; i >= 0; i--) {
      const c = new Date(now.getFullYear(), now.getMonth() - i, 1)
      cohortMonths.push(c)
    }
    return cohortMonths.map((cm) => {
      const members = (data?.customers || []).filter((c) => {
        if (!c.created_at) return false
        const cd = parseLocalDate(String(c.created_at).split(" ")[0]) || new Date(c.created_at)
        return cd.getFullYear() === cm.getFullYear() && cd.getMonth() === cm.getMonth()
      })
      const memberIds = new Set(members.map((c) => String(c.id)))
      const size = members.length
      const retention = []
      for (let mo = 0; mo < 6; mo++) {
        const target = new Date(cm.getFullYear(), cm.getMonth() + mo, 1)
        if (target > now) { retention.push(null); continue }
        if (size === 0) { retention.push(null); continue }
        // Compute M0 the same way as M1+: fraction of cohort members with
        // actual job activity in the acquisition month. The old hardcoded
        // 1.0 implied perfect activation and masked ghost-signup cohorts.
        // Months span up to 6 months back — use the job history, not just
        // the selected period's jobs (which left most cells at 0%).
        const activeInMonth = new Set(
          (data?.historyJobs || data?.jobs || [])
            .filter((j) => {
              if (isCancelledJob(j)) return false
              const jd = parseLocalDate(j.scheduled_date)
              if (!jd) return false
              if (jd.getFullYear() !== target.getFullYear() || jd.getMonth() !== target.getMonth()) return false
              return j.customer_id && memberIds.has(String(j.customer_id))
            })
            .map((j) => String(j.customer_id))
        )
        retention.push(activeInMonth.size / size)
      }
      return {
        cohort: cm.toLocaleString("en-US", { month: "short", year: "numeric" }),
        size,
        retention,
      }
    })
  })()

  // New customers per week (last 12 weeks)
  const newPerWeek = (() => {
    const buckets = Array.from({ length: 12 }, () => 0)
    const now = new Date()
    const earliest = new Date(now); earliest.setDate(earliest.getDate() - 12 * 7 + 1)
    ;(data?.customers || []).forEach((c) => {
      if (!c.created_at) return
      // parseLocalDate avoids the YYYY-MM-DD → UTC-midnight shift that was
      // dropping some customers into the wrong bucket in west-of-UTC zones.
      const cd = parseLocalDate(String(c.created_at).split(" ")[0]) || new Date(c.created_at)
      if (cd < earliest || cd > now) return
      const idx = Math.min(11, Math.floor((cd - earliest) / (7 * 24 * 60 * 60 * 1000)))
      buckets[idx] += 1
    })
    return buckets
  })()

  // No churn reason is recorded anywhere, so group lost customers by how
  // long they've been inactive instead (real data). Backend list rows carry
  // daysSinceLastJob / lastJobDate.
  const lostReasons = (() => {
    const buckets = [
      { l: "90–180 days", min: 90, max: 180 },
      { l: "6–12 months", min: 180, max: 365 },
      { l: "Over a year", min: 365, max: Infinity },
    ].map((b) => ({ ...b, v: 0 }))
    ;(m.lost?.lostCustomersList || []).forEach((c) => {
      const days = Number(c.daysSinceLastJob ?? c.days_inactive)
      const b = buckets.find((x) => days >= x.min && days < x.max)
      if (b) b.v += 1
    })
    return buckets.filter((b) => b.v > 0).map(({ l, v }) => ({ l, v }))
  })()

  return (
    <div style={{ padding: "14px 24px 24px", display: "flex", flexDirection: "column", gap: 14 }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gap: 12 }}>
        <SfKPI label="Active customers" value={m.activeCustomers} sub="all-time" accent={T.blue} />
        <SfKPI label="New · 30d" value={m.newCustomers} sub="created" accent={T.green} />
        <SfKPI label="Churn rate" value={`${churnRate}%`} sub={`${lost} with no job in 90+ days`} accent={T.red} />
        <SfKPI label="Avg LTV" value={money(m.avgLTV)} sub="lifetime spend" accent={T.purple} />
        <SfKPI label="Recurring share" value={`${recurringShare}%`} sub="of active" accent={T.teal} />
        <SfKPI
          label="Reactivated"
          value={m.reactivatedCount > 0 ? m.reactivatedCount : "0"}
          sub={m.reactivatedCount > 0 ? "returned after 90d+ gap" : "no win-backs this period"}
          accent={T.amber}
        />
      </div>

      {/* Cohort matrix */}
      <SfCard padding={false}>
        <div style={{ padding: "12px 18px", borderBottom: `1px solid ${T.borderS}`, display: "flex", alignItems: "center", gap: 10 }}>
          <Users size={14} color={T.ink2} />
          <div>
            <div style={{ fontSize: 13.5, fontWeight: 700, color: T.ink }}>Cohort retention · monthly</div>
            <div style={{ fontSize: 11, color: T.ink3, marginTop: 1 }}>% of each acquisition cohort with a job that month</div>
          </div>
          <div style={{ flex: 1 }} />
          <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 10.5, color: T.ink3 }}>
            <span>Lower</span>
            {["#F0E0D0", "#BBE5C8", "#86D3A2", "#52BC7C", T.greenDark].map((c, i) => (
              <span key={i} style={{ width: 14, height: 14, borderRadius: 3, background: c, border: `1px solid ${T.borderS}` }} />
            ))}
            <span>Higher</span>
          </div>
        </div>
        <div style={{ padding: "14px 18px" }}>
          <div style={{
            display: "grid", gridTemplateColumns: "130px 70px repeat(6, 1fr)", gap: 6, marginBottom: 6,
          }}>
            <div style={{ fontSize: 10.5, color: T.ink3, fontWeight: 700, letterSpacing: ".04em", textTransform: "uppercase" }}>Cohort</div>
            <div style={{ fontSize: 10.5, color: T.ink3, fontWeight: 700, letterSpacing: ".04em", textTransform: "uppercase", textAlign: "right", paddingRight: 4 }}>Size</div>
            {["M0", "M1", "M2", "M3", "M4", "M5"].map((mh) => (
              <div key={mh} style={{ fontSize: 10.5, color: T.ink3, fontWeight: 700, letterSpacing: ".04em", textTransform: "uppercase", textAlign: "center" }}>
                {mh}
              </div>
            ))}
          </div>
          {cohorts.map((c) => (
            <div key={c.cohort} style={{ display: "grid", gridTemplateColumns: "130px 70px repeat(6, 1fr)", gap: 6, marginBottom: 6, alignItems: "center" }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: T.ink }}>{c.cohort}</div>
              <div style={{ fontSize: 11.5, color: T.ink2, textAlign: "right", paddingRight: 4, fontVariantNumeric: "tabular-nums", fontWeight: 600 }}>{c.size}</div>
              {c.retention.map((v, i) => {
                const c2 = cohortColor(v)
                const empty = v === null || v === undefined
                return (
                  <div key={i} style={{
                    height: 38, borderRadius: 5,
                    background: empty ? "transparent" : c2,
                    border: empty ? `1px dashed ${T.borderS}` : `1px solid ${T.borderS}`,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 12, fontWeight: 700,
                    color: empty ? T.ink4 : v >= 0.65 ? "#fff" : T.ink,
                    fontVariantNumeric: "tabular-nums",
                  }}>
                    {empty ? "—" : `${Math.round(v * 100)}%`}
                  </div>
                )
              })}
            </div>
          ))}
        </div>
      </SfCard>

      {/* Per-week + segments */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <SfCard>
          <SfCardHeader title="New customers per week" subtitle="Last 12 weeks" />
          <div style={{ paddingTop: 8 }}>
            {newPerWeek.some((v) => v > 0) ? (
              <BarChart data={newPerWeek} labels={Array.from({ length: 12 }, (_, i) => `W${i + 1}`)} color={T.green} height={170} />
            ) : <EmptyChart title="No new customers in window" height={170} />}
          </div>
        </SfCard>

        <SfCard>
          <SfCardHeader title="Acquisition sources" subtitle="By lead_source field" />
          {m.sources.length ? (
            <HBarList
              data={m.sources.map((s, i) => ({ ...s, c: [T.green, T.blue, T.red, T.purple, T.amber, T.ink3][i] || T.ink3 }))}
              fmt={(v) => v}
            />
          ) : <EmptyChart title="No source data" />}
        </SfCard>
      </div>

      {/* Lost reasons */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <SfCard>
          <SfCardHeader title="Lost customers by time inactive" subtitle={`${lost} with no job in 90+ days`} />
          {lostReasons.length ? (
            <HBarList data={lostReasons.map((r, i) => ({ ...r, c: [T.amber, T.red, T.redDark][i] || T.ink3 }))} />
          ) : <EmptyChart title="No lost customers" subtitle="Everyone has had a job in the last 90 days" />}
        </SfCard>

        <SfCard>
          <SfCardHeader title="Lost customer list" subtitle="From inactive-90d threshold" />
          {(m.lost?.lostCustomersList || []).length ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {(m.lost.lostCustomersList || []).slice(0, 8).map((c, i) => {
                // Backend sends camelCase (lastJobDate / daysSinceLastJob).
                const lastJob = c.lastJobDate || c.last_job_date
                const daysOut = c.daysSinceLastJob ?? c.days_inactive
                return (
                <div key={c.customerId || c.id || i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 10px", background: T.panelAlt, borderRadius: 7 }}>
                  <SfAvatar initials={sfInitials(c.name || c.customer_name || "?")} color={T.red} size={26} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12.5, fontWeight: 600, color: T.ink }}>{c.name || c.customer_name || "Customer"}</div>
                    <div style={{ fontSize: 11, color: T.ink3 }}>
                      {lastJob
                        ? `Last job ${(parseLocalDate(String(lastJob).split(" ")[0]) || new Date(lastJob)).toLocaleDateString()}`
                        : "Inactive"}
                    </div>
                  </div>
                  <div style={{ fontSize: 11.5, color: T.redDark, fontWeight: 700 }}>{daysOut != null ? `${daysOut}d` : "—"}</div>
                </div>
                )
              })}
            </div>
          ) : <EmptyChart title="No inactive customers" />}
        </SfCard>
      </div>
    </div>
  )
}

// ══════════════════════════════════════════════════════════════════════
// SALARY TAB
// ══════════════════════════════════════════════════════════════════════

const SalaryTab = ({ m }) => {
  if (!m) return null
  const sum = m.salary?.summary || {}
  const total = sum.totalPayroll || 0
  const hourly = sum.totalHourlyPayroll || 0
  const commission = sum.totalCommissionPayroll || 0
  const memberCount = sum.memberCount || (m.salary?.memberBreakdown || []).length

  // Build dual-bar series from salary.timeSeries (payroll) + monthly revenue.
  // Right-align both to the trailing 7 months so a short payroll history
  // (common on first-run tenants) doesn't line up zero-payroll bars under
  // the earliest revenue months.
  // Backend timeSeries rows: { date: "YYYY-MM" | "YYYY-MM-DD", totalPayroll,
  // hourlyPayroll, commissionPayroll }. Sum them into the same month keys as
  // the revenue chart so each payroll bar sits under the right month.
  const payrollRows = (m.salary?.timeSeries || []).map((row) => {
    const [y, mo] = String(row.date || "").split("-").map((n) => parseInt(n, 10))
    return {
      key: Number.isFinite(y) && Number.isFinite(mo) ? `${y}-${mo - 1}` : null,
      label: Number.isFinite(y) && Number.isFinite(mo) ? new Date(y, mo - 1, 1).toLocaleString("en-US", { month: "short" }) : "—",
      value: Number(row.totalPayroll ?? row.total ?? 0) || 0,
    }
  })
  const payrollByMonth = {}
  payrollRows.forEach((r) => { if (r.key) payrollByMonth[r.key] = (payrollByMonth[r.key] || 0) + r.value })
  const dualLabels = m.monthly.map((r) => r.label)
  const dualRevenue = m.monthly.map((r) => Math.round(r.revenue))
  const dualPayroll = m.monthly.map((r) => Math.round(payrollByMonth[r.key] || 0))
  // Standalone payroll trend card
  const tsLabels = payrollRows.map((r) => r.label)
  const payrollSeries = payrollRows.map((r) => Math.round(r.value))
  const revenueSeries = dualRevenue

  const pctOfRev = m.totalRevenue > 0 ? ((total / m.totalRevenue) * 100).toFixed(1) : "—"

  return (
    <div style={{ padding: "14px 24px 24px", display: "flex", flexDirection: "column", gap: 14 }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gap: 12 }}>
        <SfKPI label="Total payroll" value={moneyShort(total)} sub={`across ${memberCount} people`} accent={T.green} />
        <SfKPI
          label="% of revenue"
          value={total > 0 && m.totalRevenue > 0 ? `${pctOfRev}%` : "—"}
          sub={total > 0 && m.totalRevenue > 0
            ? (Number(pctOfRev) >= 40 && Number(pctOfRev) <= 50 ? "healthy range" : Number(pctOfRev) > 50 ? "above typical" : "below typical")
            : "needs payroll + revenue"}
          accent={T.blue}
        />
        <SfKPI label="Hourly payroll" value={moneyShort(hourly)} sub={`${sum.hourlyOnlyCount || 0} hourly`} accent={T.purple} />
        <SfKPI label="Commission" value={moneyShort(commission)} sub={`${sum.commissionOnlyCount || 0} commission`} accent={T.amber} />
        <SfKPI label="Hybrid" value={sum.hybridCount || 0} sub="hourly + commission" accent={T.teal} />
        <SfKPI
          label="Tips + bonus"
          value={(m.totalTips + m.totalIncentives) > 0 ? moneyShort(m.totalTips + m.totalIncentives) : "$0"}
          sub={m.totalTips > 0 || m.totalIncentives > 0 ? `${moneyShort(m.totalTips)} tips · ${moneyShort(m.totalIncentives)} bonus` : "no tips logged"}
          accent={T.red}
        />
      </div>

      {/* Dual-bar payroll vs revenue */}
      <SfCard>
        <SfCardHeader
          title="Payroll vs revenue"
          subtitle="Side-by-side trend · % ratio below each pair"
          right={
            <div style={{ display: "flex", gap: 14, fontSize: 11.5, color: T.ink2 }}>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
                <span style={{ width: 10, height: 10, borderRadius: 2, background: T.green }} />Revenue
              </span>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
                <span style={{ width: 10, height: 10, borderRadius: 2, background: T.amberDark }} />Payroll
              </span>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
                <span style={{ width: 10, height: 10, borderRadius: 2, background: T.blueDark }} />% of rev
              </span>
            </div>
          }
        />
        {dualPayroll.some((v) => v > 0) || revenueSeries.some((v) => v > 0) ? (
          <DualBarChart revenue={dualRevenue} payroll={dualPayroll} labels={dualLabels} />
        ) : (
          <EmptyChart title="Not enough payroll history" height={200} />
        )}
      </SfCard>

      {/* Per-person cost table */}
      <SfCard padding={false}>
        <div style={{ padding: "12px 18px", background: T.panelAlt, borderBottom: `1px solid ${T.borderS}`, display: "flex", alignItems: "center", gap: 10 }}>
          <DollarSign size={14} color={T.ink2} />
          <div>
            <div style={{ fontSize: 13.5, fontWeight: 700, color: T.ink }}>Per-person payroll cost</div>
            <div style={{ fontSize: 11, color: T.ink3, marginTop: 1 }}>From payroll engine · this period</div>
          </div>
          <div style={{ flex: 1 }} />
          <SfButton variant="ghost" size="sm" icon={RefreshCw}>Sort: Cost ↓</SfButton>
        </div>

        <div style={{
          display: "grid", gridTemplateColumns: "1.6fr 110px 100px 110px 100px 110px 110px", gap: 14,
          padding: "10px 18px", background: T.panel, borderBottom: `1px solid ${T.borderS}`,
          fontSize: 10.5, color: T.ink3, fontWeight: 700, letterSpacing: ".05em", textTransform: "uppercase",
        }}>
          <div>Person</div>
          <div style={{ textAlign: "right" }}>Type</div>
          <div style={{ textAlign: "right" }}>Hours</div>
          <div style={{ textAlign: "right" }}>Hourly $</div>
          <div style={{ textAlign: "right" }}>Commission</div>
          <div style={{ textAlign: "right" }}>Total cost</div>
          <div style={{ textAlign: "right" }}>Cost / job</div>
        </div>
        {(m.salary?.memberBreakdown || []).length ? (m.salary.memberBreakdown).map((p, i, arr) => {
          // Backend row: { name, totalSalary, totalHourlySalary, totalCommission,
          // totalHours, jobCount, paymentMethod: 'hourly'|'commission'|'hybrid' }
          const jobs = p.jobCount ?? p.jobsCount ?? p.jobs ?? 0
          const totalCost = Number(p.totalSalary ?? p.totalPayroll ?? p.total ?? 0)
          const hourlyCost = Number(p.totalHourlySalary ?? p.hourlyPayroll ?? 0)
          const commissionCost = Number(p.totalCommission ?? p.commissionPayroll ?? 0)
          const hours = Number(p.totalHours ?? p.hours ?? 0)
          const costPerJob = jobs > 0 ? Math.round(totalCost / jobs) : null
          const name = p.name || `${p.first_name || ""} ${p.last_name || ""}`.trim() || "—"
          const method = p.paymentMethod || p.compensationType
          const type = method
            ? method.charAt(0).toUpperCase() + method.slice(1)
            : (hourlyCost > 0 && commissionCost > 0 ? "Hybrid" : commissionCost > 0 ? "Commission" : hourlyCost > 0 ? "Hourly" : "—")
          return (
            <div key={p.id || i} style={{
              display: "grid", gridTemplateColumns: "1.6fr 110px 100px 110px 100px 110px 110px", gap: 14,
              padding: "12px 18px", alignItems: "center",
              borderBottom: i < arr.length - 1 ? `1px solid ${T.borderS}` : "none",
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <SfAvatar initials={sfInitials(name)} color={[T.blue, T.purple, T.green, T.amber][i % 4]} size={28} />
                <div>
                  <div style={{ fontSize: 12.5, fontWeight: 600, color: T.ink }}>{name}</div>
                  <div style={{ fontSize: 10.5, color: T.ink3, marginTop: 1 }}>{p.role || p.subrole || "Member"}</div>
                </div>
              </div>
              <div style={{ textAlign: "right", fontSize: 12.5, color: T.ink2 }}>{type}</div>
              <div style={{ textAlign: "right", fontSize: 12, color: T.ink2, fontVariantNumeric: "tabular-nums" }}>
                {hours > 0 ? `${hours.toFixed(0)}h` : "—"}
              </div>
              <div style={{ textAlign: "right", fontSize: 13, fontWeight: 600, color: T.ink, fontVariantNumeric: "tabular-nums" }}>
                {hourlyCost > 0 ? money(hourlyCost) : "—"}
              </div>
              <div style={{ textAlign: "right", fontSize: 12, color: T.greenDark, fontVariantNumeric: "tabular-nums" }}>
                {commissionCost > 0 ? money(commissionCost) : "—"}
              </div>
              <div style={{ textAlign: "right", fontSize: 14, fontWeight: 700, color: T.ink, fontVariantNumeric: "tabular-nums", letterSpacing: "-0.01em" }}>
                {money(totalCost)}
              </div>
              <div style={{
                textAlign: "right", fontSize: 13, fontWeight: 600,
                color: costPerJob === null ? T.ink3 : costPerJob < 80 ? T.greenDark : costPerJob < 110 ? T.ink : T.amberDark,
                fontVariantNumeric: "tabular-nums",
              }}>
                {costPerJob === null ? "—" : `$${costPerJob}`}
              </div>
            </div>
          )
        }) : (
          <EmptyChart title="No payroll breakdown" subtitle="Make sure payroll engine has run for this period" />
        )}
      </SfCard>

      {/* Hourly vs Commission split */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <SfCard>
          <SfCardHeader title="Hourly vs commission split" subtitle="Where the money goes" />
          {hourly + commission > 0 ? (
            <div style={{ display: "flex", alignItems: "center", gap: 24 }}>
              <DonutChart
                data={[{ v: Math.round(hourly), c: T.blue }, { v: Math.round(commission), c: T.purple }]}
                size={140}
                label={moneyShort(hourly + commission)}
              />
              <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 12 }}>
                {[
                  { l: "Hourly $", v: hourly, c: T.blue, sub: `${sum.hourlyOnlyCount || 0} hourly` + ((sum.hybridCount || 0) ? ` + ${sum.hybridCount} hybrid share` : "") },
                  { l: "Commission $", v: commission, c: T.purple, sub: `${sum.commissionOnlyCount || 0} commission` + ((sum.hybridCount || 0) ? ` + ${sum.hybridCount} hybrid share` : "") },
                ].map((r) => {
                  const p = Math.round((r.v / Math.max(1, hourly + commission)) * 100)
                  return (
                    <div key={r.l}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, marginBottom: 3 }}>
                        <span style={{ width: 8, height: 8, borderRadius: 2, background: r.c }} />
                        <span style={{ flex: 1, color: T.ink, fontWeight: 600 }}>{r.l}</span>
                        <span style={{ color: T.ink, fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>{money(r.v)}</span>
                        <span style={{ color: T.ink3, fontVariantNumeric: "tabular-nums", minWidth: 32, textAlign: "right", fontWeight: 600 }}>{p}%</span>
                      </div>
                      <div style={{ fontSize: 10.5, color: T.ink3, marginLeft: 16, marginBottom: 6 }}>{r.sub}</div>
                      <div style={{ height: 5, background: T.panelSoft, borderRadius: 3, overflow: "hidden" }}>
                        <div style={{ width: `${p}%`, height: "100%", background: r.c, borderRadius: 3 }} />
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          ) : <EmptyChart title="No payroll split data" />}
        </SfCard>

        <SfCard>
          <SfCardHeader title="Payroll trend" subtitle={`${payrollSeries.length} periods`} />
          <div style={{ paddingTop: 8 }}>
            {payrollSeries.some((v) => v > 0) ? (
              <BarChart data={payrollSeries} labels={tsLabels} color={T.amberDark} height={170} valueFmt={moneyShort} />
            ) : <EmptyChart title="No payroll history" height={170} />}
          </div>
          {payrollSeries.some((v) => v > 0) && m.totalRevenue > 0 && (() => {
            const latestPayroll = payrollSeries[payrollSeries.length - 1] || 0
            const avgPayroll = payrollSeries.reduce((a, b) => a + b, 0) / payrollSeries.length
            const latestVsAvg = avgPayroll > 0 ? Math.round(((latestPayroll - avgPayroll) / avgPayroll) * 100) : 0
            const bg = Math.abs(latestVsAvg) >= 20 ? T.amberSoft : T.panelAlt
            const bd = Math.abs(latestVsAvg) >= 20 ? `${alpha(T.amber, "33")}` : T.borderS
            const fg = Math.abs(latestVsAvg) >= 20 ? T.amberDark : T.ink2
            return (
              <div style={{ marginTop: 10, padding: "8px 12px", background: bg, border: `1px solid ${bd}`, borderRadius: 6, fontSize: 11.5, color: fg, lineHeight: 1.5 }}>
                <b>Latest period:</b> {moneyShort(latestPayroll)} payroll vs {moneyShort(avgPayroll)} avg ({latestVsAvg > 0 ? "+" : ""}{latestVsAvg}%)
              </div>
            )
          })()}
        </SfCard>
      </div>
    </div>
  )
}

// ══════════════════════════════════════════════════════════════════════
// CONVERSION TAB
// ══════════════════════════════════════════════════════════════════════

const ConversionTab = ({ m, data }) => {
  if (!m) return null
  const c = m.conversion || {}
  const s = c.summary || {}

  const funnelStages = c.funnel || []
  const stagesForRender = funnelStages.length
    ? funnelStages.map((stage, i) => ({
        label: stage.label,
        count: stage.count,
        color: [T.ink2, T.blue, T.purple, T.amber, T.greenDark][i] || T.green,
        // null for stage 0 means "no reference" — the renderer shows the
        // raw count on that row instead of a misleading 100%.
        pct: i === 0 ? null : (funnelStages[0].count > 0 ? stage.count / funnelStages[0].count : 0),
      }))
    : null

  // Backend shapes (GET /analytics/conversion):
  //   summary: { totalLeads, convertedLeads, conversionRate (already a %),
  //              avgTimeToConversion (days), totalLeadValue, convertedLeadValue }
  //   bySource: { "<source>": { total, converted, conversionRate, ... } }
  //   timeSeries: [{ date, totalLeads, convertedLeads, conversionRate (%) }]
  const bySourceList = Object.entries(c.bySource || {})
    .map(([source, v]) => ({
      l: source,
      total: Number(v.total ?? v.totalLeads ?? 0) || 0,
      won: Number(v.converted ?? v.won ?? 0) || 0,
    }))
    .filter((r) => r.total > 0)
    .sort((a, b) => b.total - a.total)

  const tsValues = (c.timeSeries || []).map((p) => Math.round(Number(p.conversionRate) || 0))
  const tsLabels = (c.timeSeries || []).map((p) => {
    const dt = parseLocalDate(p.date)
    return dt ? `${dt.toLocaleString("en-US", { month: "short" })} ${dt.getDate()}` : (p.date || "—")
  })

  // Local fallback when the pipeline module isn't used (no opportunities):
  // customers with at least one job vs all customers.
  const hasPipeline = Number(s.totalLeads) > 0
  const localLeads = (data?.customers || []).length
  const localConverted = (data?.customers || []).filter((cu) => (m.jobsByCustomerAllTime?.[cu.id] || 0) > 0).length
  const kpiLeads = hasPipeline ? s.totalLeads : (localLeads > 0 ? localLeads : "—")
  const kpiConvRate = hasPipeline
    ? `${Number(s.conversionRate || 0).toFixed(1)}%`
    : (localLeads > 0 ? `${((localConverted / localLeads) * 100).toFixed(1)}%` : "—")
  const kpiConvSub = hasPipeline ? `${s.convertedLeads || 0} became customers` : (localLeads > 0 ? "customers with ≥1 job / total customers" : "no pipeline data")
  const notConverted = hasPipeline ? (Number(s.totalLeads) - Number(s.convertedLeads || 0)) : (localLeads > 0 ? localLeads - localConverted : "—")

  return (
    <div style={{ padding: "14px 24px 24px", display: "flex", flexDirection: "column", gap: 14 }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gap: 12 }}>
        <SfKPI label="Opportunities" value={kpiLeads} sub={hasPipeline ? "created this period" : (localLeads > 0 ? "from customers list" : "no pipeline data")} accent={T.blue} />
        <SfKPI label="Opportunity → customer" value={kpiConvRate} sub={kpiConvSub} accent={T.green} />
        <SfKPI label="Avg time to convert" value={hasPipeline && Number(s.convertedLeads) > 0 ? `${Number(s.avgTimeToConversion || 0).toFixed(1)}d` : "—"} sub="created → converted" accent={T.purple} />
        <SfKPI label="Lead value" value={hasPipeline ? moneyShort(s.totalLeadValue || 0) : "—"} sub="all opportunities this period" accent={T.amber} />
        <SfKPI label="Won value" value={hasPipeline ? moneyShort(s.convertedLeadValue || 0) : "—"} sub="from converted opportunities" accent={T.teal} />
        <SfKPI label="Not converted" value={notConverted} sub={hasPipeline ? "open or lost" : (localLeads > 0 ? "customers without a job" : "")} accent={T.red} />
      </div>

      {/* Funnel */}
      <SfCard padding={false}>
        <div style={{ padding: "12px 18px", borderBottom: `1px solid ${T.borderS}`, display: "flex", alignItems: "center", gap: 10 }}>
          <Workflow size={14} color={T.ink2} />
          <div>
            <div style={{ fontSize: 13.5, fontWeight: 700, color: T.ink }}>Opportunities by stage</div>
            <div style={{ fontSize: 11, color: T.ink3, marginTop: 1 }}>Where this period's opportunities currently sit</div>
          </div>
          <div style={{ flex: 1 }} />
          {stagesForRender && hasPipeline && (
            <>
              <span style={{ fontSize: 11, color: T.ink3, fontWeight: 600, letterSpacing: ".04em", textTransform: "uppercase" }}>Converted</span>
              <span style={{ fontSize: 20, fontWeight: 700, color: T.greenDark, fontVariantNumeric: "tabular-nums" }}>
                {Number(s.conversionRate || 0).toFixed(1)}%
              </span>
            </>
          )}
        </div>
        {stagesForRender ? (
          <div style={{ padding: "20px 60px", display: "flex", flexDirection: "column", gap: 6 }}>
            {stagesForRender.map((stage, i) => {
              // Stage 0 has no "of starting" reference, so size its block
              // like the widest (100%) and skip the pct label beside it.
              const widthPct = stage.pct == null ? 100 : stage.pct * 100
              return (
                <div key={stage.label} style={{ display: "flex", alignItems: "center", gap: 14 }}>
                  <div style={{ flex: 1, display: "flex", justifyContent: "center", position: "relative" }}>
                    <div style={{
                      width: `${Math.max(widthPct, 12)}%`, height: 54,
                      // Solid colour: stage.color is a CSS var, and "var(--x)dd"
                      // is invalid CSS, which dropped the background entirely
                      // (white label on white = invisible bars).
                      background: stage.color,
                      clipPath: "polygon(4% 0, 96% 0, 92% 100%, 8% 100%)",
                      display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 1,
                    }}>
                      <span style={{ color: "#fff", fontSize: 11, fontWeight: 600, letterSpacing: ".04em", textTransform: "uppercase", opacity: .9 }}>
                        {stage.label}
                      </span>
                      <span style={{ color: "#fff", fontSize: 20, fontWeight: 700, letterSpacing: "-0.02em", fontVariantNumeric: "tabular-nums" }}>
                        {stage.count}
                      </span>
                    </div>
                  </div>
                  <div style={{ width: 160, textAlign: "left" }}>
                    {i > 0 ? (
                      <>
                        <div style={{ fontSize: 13, fontWeight: 700, color: T.ink, fontVariantNumeric: "tabular-nums" }}>
                          {Math.round((stage.pct ?? 0) * 100)}% <span style={{ color: T.ink3, fontWeight: 500, fontSize: 11 }}>of largest stage</span>
                        </div>
                      </>
                    ) : (
                      <div style={{ fontSize: 10.5, color: T.ink3, fontWeight: 700, letterSpacing: ".04em", textTransform: "uppercase" }}>
                        Largest stage
                      </div>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        ) : <EmptyChart icon={Target} title="No funnel data" subtitle="Backend conversion endpoint returned empty" />}
      </SfCard>

      {/* Win rate by source + Lost reasons */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <SfCard>
          <SfCardHeader title="Win rate by source" subtitle="Among leads with a final outcome" />
          {bySourceList.length ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 11 }}>
              {bySourceList.map((r) => {
                const rate = Math.round((r.won / r.total) * 100)
                const c2 = rate >= 50 ? T.greenDark : rate >= 30 ? T.amberDark : T.redDark
                return (
                  <div key={r.l}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, marginBottom: 4 }}>
                      <span style={{ flex: 1, color: T.ink, fontWeight: 600 }}>{r.l}</span>
                      <span style={{ color: T.ink3, fontSize: 11, fontVariantNumeric: "tabular-nums", fontWeight: 500 }}>
                        {r.won} / {r.total}
                      </span>
                      <span style={{ color: c2, fontWeight: 700, fontVariantNumeric: "tabular-nums", minWidth: 42, textAlign: "right" }}>
                        {rate}%
                      </span>
                    </div>
                    <div style={{ height: 6, background: T.panelSoft, borderRadius: 3, overflow: "hidden" }}>
                      <div style={{ width: `${rate}%`, height: "100%", background: c2, borderRadius: 3 }} />
                    </div>
                  </div>
                )
              })}
            </div>
          ) : <EmptyChart title="No source breakdown" />}
        </SfCard>

        <SfCard>
          <SfCardHeader title="Conversion rate trend" subtitle={`${tsValues.length} week${tsValues.length === 1 ? "" : "s"} · % converted`} />
          <div style={{ paddingTop: 8 }}>
            {tsValues.some((v) => v > 0) ? (
              <BarChart data={tsValues} labels={tsLabels} color={T.greenDark} height={170} valueFmt={(v) => `${v}%`} />
            ) : <EmptyChart title="No trend data" height={170} />}
          </div>
        </SfCard>
      </div>
    </div>
  )
}

// ══════════════════════════════════════════════════════════════════════
// MARKETING SPEND BY CHANNEL
// ══════════════════════════════════════════════════════════════════════

const CHANNEL_META = {
  thumbtack:  { label: "Thumbtack",  color: T.amber,  syncModel: "auto",    note: "Derived from actual per-lead cost" },
  yelp:       { label: "Yelp",       color: T.red,    syncModel: "manual",  note: "Manual monthly entry — Yelp has no cost API" },
  google_ads: { label: "Google Ads", color: T.blue,   syncModel: "future",  note: "Integration pending" },
  meta_ads:   { label: "Meta Ads",   color: T.purple, syncModel: "future",  note: "Integration pending" },
  google_lsa: { label: "Google LSA", color: T.teal,   syncModel: "future",  note: "Integration pending" },
  other:      { label: "Other",      color: T.ink3,   syncModel: "manual",  note: "" },
}

const CHANNEL_ORDER = ["thumbtack", "yelp", "google_ads", "meta_ads", "google_lsa", "other"]

const centsToDollars = (c) => (c == null ? null : c / 100)
const dollarsToCents = (d) => (d == null || d === "" ? null : Math.round(parseFloat(d) * 100))
const monthKeyOfPeriodStart = (ps) => ps ? ps.slice(0, 7) : null   // 'YYYY-MM'

// ── Modal for edit / manual entry / reset override
const MarketingSpendEditor = ({ open, channel, existing, defaultMonth, onSave, onReset, onClose, saving }) => {
  const meta = CHANNEL_META[channel] || CHANNEL_META.other
  const startMonth = existing ? monthKeyOfPeriodStart(existing.period_start) : defaultMonth
  const [period, setPeriod] = useState(startMonth || new Date().toISOString().slice(0, 7))
  const [amountDollars, setAmountDollars] = useState(
    existing?.amount_dollars != null ? String(existing.amount_dollars) : ""
  )
  const [note, setNote] = useState(existing?.metadata?.manual_note || "")

  useEffect(() => {
    if (!open) return
    setPeriod(existing ? monthKeyOfPeriodStart(existing.period_start) : (defaultMonth || new Date().toISOString().slice(0, 7)))
    setAmountDollars(existing?.amount_dollars != null ? String(existing.amount_dollars) : "")
    setNote(existing?.metadata?.manual_note || "")
  }, [open, existing, defaultMonth])

  if (!open) return null

  const canSubmit = period && amountDollars !== "" && parseFloat(amountDollars) >= 0

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, background: "rgba(15,23,42,.55)",
        display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: T.panel, border: `1px solid ${T.border}`, borderRadius: 12,
          padding: 22, minWidth: 460, maxWidth: 520, boxShadow: "0 12px 32px rgba(15,23,42,.18)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
          <span style={{ width: 12, height: 12, borderRadius: 3, background: meta.color, flexShrink: 0 }} />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: T.ink }}>
              {existing ? "Edit " : "Add "}{meta.label} spend
            </div>
            <div style={{ fontSize: 11.5, color: T.ink3, marginTop: 1 }}>{meta.note}</div>
          </div>
          <button onClick={onClose} style={{ background: "transparent", border: "none", cursor: "pointer", color: T.ink3, padding: 4 }}>
            <X size={16} />
          </button>
        </div>

        {existing && existing.is_manual_override && existing.reported_amount_dollars != null && (
          <div style={{ marginBottom: 12, padding: "8px 10px", background: T.amberSoft, border: `1px solid ${alpha(T.amber, "33")}`, borderRadius: 7, fontSize: 12, color: T.amberDark, display: "flex", alignItems: "center", gap: 8 }}>
            <Zap size={12} />
            <span style={{ flex: 1 }}>
              Reported by {existing.source_type}: <b>${existing.reported_amount_dollars.toFixed(2)}</b> · Using manual: <b>${(existing.amount_dollars || 0).toFixed(2)}</b>
            </span>
            <button
              onClick={onReset}
              style={{ background: "transparent", border: `1px solid ${alpha(T.amberDark, "55")}`, borderRadius: 5, padding: "3px 8px", fontSize: 11, color: T.amberDark, cursor: "pointer", fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 4 }}
            >
              <RotateCcw size={11} /> Reset
            </button>
          </div>
        )}

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 14 }}>
          <div>
            <label style={labelStyle}>Period</label>
            <input
              type="month"
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              disabled={!!existing}   // period is the key; changing it needs a new row
              style={{ ...inputStyle, opacity: existing ? .6 : 1 }}
            />
          </div>
          <div>
            <label style={labelStyle}>Amount (USD)</label>
            <input
              type="number" step="0.01" min="0" placeholder="0.00"
              value={amountDollars}
              onChange={(e) => setAmountDollars(e.target.value)}
              style={inputStyle}
            />
          </div>
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={labelStyle}>Note (optional)</label>
          <input
            type="text" placeholder="e.g. 'includes premium pack'"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            style={inputStyle}
          />
        </div>

        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <SfButton variant="ghost" size="md" onClick={onClose}>Cancel</SfButton>
          <SfButton
            variant="primary" size="md"
            onClick={() => onSave({ period, amount_cents: dollarsToCents(amountDollars), note })}
            disabled={!canSubmit || saving}
          >
            {saving ? "Saving…" : (existing ? "Save changes" : "Add spend")}
          </SfButton>
        </div>
      </div>
    </div>
  )
}

// ── Marketing spend by channel (main table on Expenses tab)
const MarketingSpendTable = ({ marketingSpend, adsSpendBySource, dateRange, onChanged }) => {
  const [editorOpen, setEditorOpen] = useState(false)
  const [editorChannel, setEditorChannel] = useState(null)
  const [editorExisting, setEditorExisting] = useState(null)
  const [saving, setSaving] = useState(false)
  const [syncing, setSyncing] = useState(false)
  const [error, setError] = useState("")

  // Reporting period label: "Aug 2026" if the range fits one month; else the range dates.
  const rangeLabel = (() => {
    if (!dateRange?.startStr || !dateRange?.endStr) return ""
    const a = new Date(dateRange.startStr), b = new Date(dateRange.endStr)
    if (a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth()) {
      return a.toLocaleString("en-US", { month: "short", year: "numeric" })
    }
    return `${dateRange.startStr} → ${dateRange.endStr}`
  })()

  const defaultMonth = dateRange?.endStr ? dateRange.endStr.slice(0, 7) : new Date().toISOString().slice(0, 7)

  // Roll up marketing_spend rows by source across the current period.
  // If a source has 0 rows, we still render a row (empty state per channel).
  const bySourceRollup = {}
  for (const r of marketingSpend || []) {
    const src = r.source
    if (!bySourceRollup[src]) {
      bySourceRollup[src] = {
        source: src,
        totalCents: 0,
        reportedCents: null,
        anyManualOverride: false,
        rows: [],
        sourceTypes: new Set(),
      }
    }
    const b = bySourceRollup[src]
    b.totalCents += r.amount_cents || 0
    if (r.reported_amount_cents != null) {
      b.reportedCents = (b.reportedCents || 0) + r.reported_amount_cents
    }
    if (r.is_manual_override) b.anyManualOverride = true
    b.sourceTypes.add(r.source_type)
    b.rows.push(r)
  }

  // Lead counts + CPL come from adsSpendBySource (backend already computed).
  const leadsBySource = {}
  for (const r of adsSpendBySource || []) {
    // The backend groups by opportunities.source which may differ in casing
    // from marketing_spend.source. Normalize.
    const key = String(r.source).toLowerCase()
    leadsBySource[key] = { count: r.count || 0 }
  }

  const handleOpenEditor = (channel, existing) => {
    setEditorChannel(channel)
    setEditorExisting(existing || null)
    setEditorOpen(true)
    setError("")
  }

  const handleSave = async ({ period, amount_cents, note }) => {
    if (amount_cents == null) return
    setSaving(true); setError("")
    try {
      if (editorExisting) {
        await marketingSpendAPI.patch(editorExisting.id, { amount_cents, note })
      } else {
        await marketingSpendAPI.upsertManual({
          source: editorChannel, period, amount_cents, note,
        })
      }
      setEditorOpen(false)
      await onChanged?.()
    } catch (e) {
      setError(e?.response?.data?.error || "Failed to save")
    } finally {
      setSaving(false)
    }
  }

  const handleReset = async () => {
    if (!editorExisting) return
    setSaving(true); setError("")
    try {
      await marketingSpendAPI.resetOverride(editorExisting.id)
      setEditorOpen(false)
      await onChanged?.()
    } catch (e) {
      setError(e?.response?.data?.error || "Failed to reset override")
    } finally {
      setSaving(false)
    }
  }

  // Backfill flow: dry-run → confirm → apply + materialize.
  // "Sync from LeadBridge" = pull actual TT per-lead cost from LB and, if
  // there's real work to do, prompt to apply. When apply=true the same
  // endpoint also runs the monthly materializer so the table updates.
  const handleSyncThumbtack = async () => {
    setSyncing(true); setError("")
    try {
      // 1. Dry-run — no writes, just counters. Uses tenant range on the server
      //    (unbounded — we want everything historically) so the operator can
      //    make a whole-history decision, not a period one.
      const dryRun = await marketingSpendAPI.backfillThumbtack({ apply: false })
      const bf = dryRun.backfill || {}
      if (bf.error) throw new Error(bf.message || bf.error)

      const patchable = bf.proposals || 0
      const hasExistingCost = (bf.already_populated || 0) > 0

      // No linked opportunities at all → nothing we can do.
      if (bf.eligible === 0) {
        setError("No LeadBridge-linked Thumbtack opportunities in SF yet.")
        return
      }

      // Nothing to backfill AND no existing cost → literally no TT cost data
      // to materialize. Tell the user what LB returned.
      if (patchable === 0 && !hasExistingCost) {
        setError(
          `No Thumbtack cost data available. Counters: eligible=${bf.eligible} lb_cost_unavailable=${bf.lb_cost_unavailable} unmatched=${bf.unmatched}. LB hasn't hydrated per-lead cost for these leads.`
        )
        return
      }

      // 2. Build the confirmation message. Two paths:
      //    (a) patchable > 0 → normal backfill + materialize
      //    (b) patchable == 0 but existing cost → materialize-only
      let summary
      if (patchable > 0) {
        summary = `LeadBridge backfill will update ${patchable} opportunities and materialize monthly spend:\n\n` +
          `• Eligible (linked): ${bf.eligible}\n` +
          `• Will be updated: ${patchable}\n` +
          `• Already had cost: ${bf.already_populated}\n` +
          `• LB has no cost: ${bf.lb_cost_unavailable}\n` +
          `• Unmatched in LB: ${bf.unmatched}\n\n` +
          `Existing costs will NOT be overwritten. Apply?`
      } else {
        summary = `Nothing new to backfill, but ${bf.already_populated} opportunities already have Thumbtack cost. Materialize them into monthly Marketing spend rows now?`
      }
      if (!window.confirm(summary)) return

      // 3. Apply + materialize monthly rows (endpoint runs materializer when apply=true).
      const applied = await marketingSpendAPI.backfillThumbtack({ apply: true, materialize: true })
      const ab = applied.backfill || {}
      const am = applied.materialize || {}
      await onChanged?.()
      window.alert(
        `Sync complete.\n\n` +
        (patchable > 0 ? `Updated ${ab.updated} opportunities. Failed: ${ab.failed}.\n` : "") +
        (am ? `Marketing spend rows: ${am.rowsCreated} created, ${am.rowsUpdated} updated. Skipped ${am.rowsSkippedNoCoverage || 0} months with no cost data.\n` : "") +
        `Manual overrides preserved.`
      )
    } catch (e) {
      setError(e?.response?.data?.error || e?.message || "Backfill failed")
    } finally {
      setSyncing(false)
    }
  }

  return (
    <>
      <SfCard padding={false}>
        <div style={{ padding: "12px 18px", borderBottom: `1px solid ${T.borderS}`, display: "flex", alignItems: "center", gap: 10 }}>
          <Megaphone size={14} color={T.ink2} />
          <div>
            <div style={{ fontSize: 13.5, fontWeight: 700, color: T.ink }}>Marketing spend by channel</div>
            <div style={{ fontSize: 11, color: T.ink3, marginTop: 1 }}>
              {rangeLabel} · CPL is spend ÷ leads acquired in the same period
            </div>
          </div>
          <div style={{ flex: 1 }} />
          <SfButton variant="ghost" size="sm" icon={syncing ? RefreshCw : Zap} onClick={handleSyncThumbtack} disabled={syncing}>
            {syncing ? "Syncing…" : "Sync from LeadBridge"}
          </SfButton>
        </div>

        {error && (
          <div style={{ padding: "8px 18px", background: T.redSoft, borderBottom: `1px solid ${T.borderS}`, color: T.redDark, fontSize: 12 }}>
            {error}
          </div>
        )}

        <div style={{
          display: "grid", gridTemplateColumns: "1.4fr 130px 130px 100px 110px 130px 90px", gap: 10,
          padding: "10px 18px", background: T.panelAlt, borderBottom: `1px solid ${T.borderS}`,
          fontSize: 10.5, color: T.ink3, fontWeight: 700, letterSpacing: ".05em", textTransform: "uppercase",
        }}>
          <div>Channel</div>
          <div style={{ textAlign: "right" }}>Spend</div>
          <div>Source</div>
          <div style={{ textAlign: "right" }}>Leads</div>
          <div style={{ textAlign: "right" }}>CPL</div>
          <div>Reported</div>
          <div style={{ textAlign: "right" }}>Actions</div>
        </div>

        {CHANNEL_ORDER
          .filter((source) => {
            // Hide "Integration pending" rows that have no spend AND no leads
            // yet — they were adding visual clutter on tenants without those
            // integrations enabled. Rows with real data continue to show.
            const meta = CHANNEL_META[source]
            const roll = bySourceRollup[source]
            const leadCount = leadsBySource[source]?.count || 0
            const hasData = roll != null || leadCount > 0
            if (meta.syncModel === "future" && !hasData) return false
            return true
          })
          .map((source, i, visibleOrder) => {
          const meta = CHANNEL_META[source]
          const roll = bySourceRollup[source]
          const spendCents = roll?.totalCents
          const leadCount = leadsBySource[source]?.count || 0
          const cplCents = spendCents != null && leadCount > 0 ? Math.round(spendCents / leadCount) : null
          const existing = roll?.rows?.[0]     // single-month view — one row per source
          const hasData = roll != null || leadCount > 0
          const notConnected = meta.syncModel === "future" && !hasData

          return (
            <div key={source} style={{
              display: "grid", gridTemplateColumns: "1.4fr 130px 130px 100px 110px 130px 90px", gap: 10,
              padding: "11px 18px", alignItems: "center",
              borderBottom: i < visibleOrder.length - 1 ? `1px solid ${T.borderS}` : "none",
              opacity: notConnected ? .6 : 1,
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
                <span style={{ width: 10, height: 10, borderRadius: 2, background: meta.color, flexShrink: 0 }} />
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 12.5, fontWeight: 600, color: T.ink }}>{meta.label}</div>
                  <div style={{ fontSize: 10.5, color: T.ink3, marginTop: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {meta.note}
                  </div>
                </div>
              </div>

              <div style={{ textAlign: "right", fontSize: 13.5, fontWeight: 700, color: spendCents != null ? T.ink : T.ink3, fontVariantNumeric: "tabular-nums" }}>
                {spendCents != null ? money(spendCents / 100) : "—"}
              </div>

              <div>
                <SourceBadge source={source} roll={roll} notConnected={notConnected} />
              </div>

              <div style={{ textAlign: "right", fontSize: 12.5, color: T.ink2, fontVariantNumeric: "tabular-nums" }}>
                {leadCount > 0 ? leadCount : "—"}
              </div>

              <div style={{ textAlign: "right", fontSize: 12.5, fontWeight: 600, color: cplCents != null ? T.ink : T.ink3, fontVariantNumeric: "tabular-nums" }}>
                {cplCents != null ? `$${(cplCents / 100).toFixed(2)}` : "—"}
              </div>

              <div style={{ fontSize: 11, color: T.ink3, fontVariantNumeric: "tabular-nums" }}>
                {roll?.reportedCents != null ? (
                  <span title="Amount most-recently reported by upstream">
                    ${(roll.reportedCents / 100).toFixed(2)}
                  </span>
                ) : "—"}
              </div>

              <div style={{ display: "flex", justifyContent: "flex-end", gap: 4 }}>
                {!notConnected && (
                  <button
                    onClick={() => handleOpenEditor(source, existing)}
                    title={existing ? "Edit" : "Add spend"}
                    style={{ background: "transparent", border: "none", cursor: "pointer", color: T.ink3, padding: 4, borderRadius: 4 }}
                    onMouseEnter={(e) => { e.currentTarget.style.color = T.blueDark }}
                    onMouseLeave={(e) => { e.currentTarget.style.color = T.ink3 }}
                  >
                    {existing ? <Pencil size={13} /> : <Plus size={13} />}
                  </button>
                )}
              </div>
            </div>
          )
        })}
      </SfCard>

      <MarketingSpendEditor
        open={editorOpen}
        channel={editorChannel}
        existing={editorExisting}
        defaultMonth={defaultMonth}
        onSave={handleSave}
        onReset={handleReset}
        onClose={() => setEditorOpen(false)}
        saving={saving}
      />
    </>
  )
}

const SourceBadge = ({ source, roll, notConnected }) => {
  const meta = CHANNEL_META[source] || CHANNEL_META.other
  if (notConnected) {
    return <span style={{ fontSize: 10.5, color: T.ink3, fontWeight: 600, padding: "3px 8px", background: T.panelAlt, borderRadius: 12, border: `1px solid ${T.borderS}` }}>Not connected</span>
  }
  if (!roll) {
    return <span style={{ fontSize: 10.5, color: T.ink3, fontWeight: 600, padding: "3px 8px", background: T.panelAlt, borderRadius: 12, border: `1px solid ${T.borderS}` }}>No data</span>
  }
  if (roll.anyManualOverride) {
    return (
      <span title="A manual override is active; reported value still tracked" style={{ fontSize: 10.5, color: T.amberDark, fontWeight: 700, padding: "3px 8px", background: T.amberSoft, borderRadius: 12, border: `1px solid ${alpha(T.amber, "44")}`, display: "inline-flex", alignItems: "center", gap: 4 }}>
        <Pencil size={9} /> Manual
      </span>
    )
  }
  if (roll.sourceTypes.has("derived_from_opportunities")) {
    return (
      <span title="Derived from per-lead cost on opportunities" style={{ fontSize: 10.5, color: T.greenDark, fontWeight: 700, padding: "3px 8px", background: T.greenSoft, borderRadius: 12, border: `1px solid ${alpha(T.green, "44")}`, display: "inline-flex", alignItems: "center", gap: 4 }}>
        <Zap size={9} /> Synced
      </span>
    )
  }
  if (roll.sourceTypes.has("manual")) {
    return (
      <span style={{ fontSize: 10.5, color: T.blueDark, fontWeight: 700, padding: "3px 8px", background: T.blueSoft, borderRadius: 12, border: `1px solid ${alpha(T.blue, "44")}` }}>
        Manual
      </span>
    )
  }
  return <span style={{ fontSize: 10.5, color: T.ink2, fontWeight: 700, padding: "3px 8px", background: T.panelAlt, borderRadius: 12, border: `1px solid ${T.borderS}` }}>Synced</span>
}

// ══════════════════════════════════════════════════════════════════════
// EXPENSES TAB
// ══════════════════════════════════════════════════════════════════════

const CATEGORY_LABELS = {
  rent: "Rent", insurance: "Insurance", software: "Software / SaaS",
  vehicle: "Vehicle", fuel: "Fuel", marketing: "Marketing (non-LB)",
  supplies: "Supplies", utilities: "Utilities", phone: "Phone",
  other: "Other",
}
const CADENCE_LABELS = {
  one_off: "One-off", weekly: "Weekly", monthly: "Monthly", yearly: "Yearly",
}
const CATEGORY_COLORS = [T.blue, T.purple, T.green, T.amber, T.teal, T.red, T.blueDark, T.purpleSoft, T.tealSoft, T.ink3]

const inputStyle = {
  width: "100%", padding: "8px 10px", fontSize: 13,
  border: `1px solid ${T.borderS}`, borderRadius: 6,
  background: T.panel, color: T.ink, outline: "none",
}
const labelStyle = {
  fontSize: 10.5, color: T.ink3, fontWeight: 700,
  letterSpacing: ".04em", textTransform: "uppercase", marginBottom: 4, display: "block",
}

const AddExpenseRow = ({ onSubmit, submitting }) => {
  const today = new Date().toISOString().split("T")[0]
  const [form, setForm] = useState({
    name: "", amount: "", category: "rent", cadence: "monthly", start_date: today, note: "",
  })
  const set = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }))
  const canSubmit = form.name.trim() && parseFloat(form.amount) >= 0 && form.start_date

  return (
    <div style={{
      display: "grid",
      gridTemplateColumns: "1.4fr 100px 130px 120px 130px 1fr 90px",
      gap: 10, padding: "12px 18px", background: T.panelAlt, borderTop: `1px solid ${T.borderS}`, alignItems: "end",
    }}>
      <div>
        <label style={labelStyle}>Name</label>
        <input style={inputStyle} placeholder="Office rent, GSuite…" value={form.name} onChange={set("name")} />
      </div>
      <div>
        <label style={labelStyle}>Amount</label>
        <input style={inputStyle} type="number" step="0.01" min="0" placeholder="0.00" value={form.amount} onChange={set("amount")} />
      </div>
      <div>
        <label style={labelStyle}>Category</label>
        <select style={inputStyle} value={form.category} onChange={set("category")}>
          {Object.entries(CATEGORY_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
      </div>
      <div>
        <label style={labelStyle}>Cadence</label>
        <select style={inputStyle} value={form.cadence} onChange={set("cadence")}>
          {Object.entries(CADENCE_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
      </div>
      <div>
        <label style={labelStyle}>Start</label>
        <input style={inputStyle} type="date" value={form.start_date} onChange={set("start_date")} />
      </div>
      <div>
        <label style={labelStyle}>Note</label>
        <input style={inputStyle} placeholder="Optional" value={form.note} onChange={set("note")} />
      </div>
      <SfButton
        variant="primary" size="md" icon={Plus}
        onClick={() => canSubmit && onSubmit({ ...form, amount: parseFloat(form.amount) })}
        disabled={!canSubmit || submitting}
      >
        Add
      </SfButton>
    </div>
  )
}

const ExpensesTab = ({ m, data, onExpensesChanged }) => {
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState("")

  if (!m) return null

  const payrollTotal = Number(m.salary?.summary?.totalPayroll || 0)
  const adsTotal = Number(m.adsSpend?.summary?.totalSpend || 0)
  const jobExpensesTotal = Number(m.expensesSummary?.summary?.jobExpensesTotal || 0)
  const businessTotal = Number(m.expensesSummary?.summary?.businessExpensesTotal || 0)
  const combinedTotal = payrollTotal + adsTotal + jobExpensesTotal + businessTotal
  const grossProfit = m.totalRevenue - combinedTotal
  const grossMarginPct = m.totalRevenue > 0 ? (grossProfit / m.totalRevenue) * 100 : 0

  const donutData = [
    { l: "Payroll",          v: payrollTotal,      c: T.blue },
    { l: "Ads (LeadBridge)", v: adsTotal,          c: T.amber },
    { l: "Job reimbursements", v: jobExpensesTotal, c: T.teal },
    { l: "Overhead",         v: businessTotal,     c: T.purple },
  ].filter((x) => x.v > 0)

  const bySource = m.adsSpend?.bySource || []
  const monthlyAds = m.adsSpend?.monthly || []

  const recurringList = data?.businessExpenses || []
  const businessByCategoryEntries = Object.entries(m.expensesSummary?.businessByCategory || {})
    .map(([k, v]) => ({ k, l: CATEGORY_LABELS[k] || k, v: Number(v) }))
    .sort((a, b) => b.v - a.v)

  const handleAdd = async (payload) => {
    setError("")
    setSubmitting(true)
    try {
      await businessExpensesAPI.create(payload)
      await onExpensesChanged?.()
    } catch (e) {
      setError(e?.response?.data?.error || "Failed to add expense")
    } finally {
      setSubmitting(false)
    }
  }

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this expense?")) return
    setError("")
    try {
      await businessExpensesAPI.delete(id)
      await onExpensesChanged?.()
    } catch (e) {
      setError(e?.response?.data?.error || "Failed to delete expense")
    }
  }

  return (
    <div style={{ padding: "14px 24px 24px", display: "flex", flexDirection: "column", gap: 14 }}>
      {/* KPI strip */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gap: 12 }}>
        <SfKPI label="Total costs"      value={moneyShort(combinedTotal)}   sub="all sources this period" accent={T.red} />
        <SfKPI label="Payroll"          value={moneyShort(payrollTotal)}    sub={m.totalRevenue > 0 ? `${((payrollTotal / m.totalRevenue) * 100).toFixed(0)}% of revenue` : "—"} accent={T.blue} />
        <SfKPI label="Ads · LeadBridge" value={moneyShort(adsTotal)}        sub={`${m.adsSpend?.summary?.opportunityCount || 0} leads`} accent={T.amber} />
        <SfKPI label="Job reimburse"    value={moneyShort(jobExpensesTotal)} sub={`${m.expensesSummary?.summary?.jobExpensesCount || 0} approved`} accent={T.teal} />
        <SfKPI label="Overhead"         value={moneyShort(businessTotal)}   sub={`${(recurringList || []).filter((r) => r.is_active).length} active`} accent={T.purple} />
        <SfKPI
          label="Gross margin"
          value={m.totalRevenue > 0 ? `${grossMarginPct.toFixed(1)}%` : "—"}
          sub={`${moneyShort(grossProfit)} profit`}
          accent={grossProfit >= 0 ? T.green : T.red}
        />
      </div>

      {error && (
        <div style={{ padding: "8px 12px", background: T.redSoft, border: `1px solid ${alpha(T.red, "33")}`, borderRadius: 6, color: T.redDark, fontSize: 12.5 }}>
          {error}
        </div>
      )}

      {/* Split donut + LB source breakdown */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <SfCard>
          <SfCardHeader title="Cost breakdown" subtitle="Where the money goes" />
          {donutData.length ? (
            <div style={{ display: "flex", alignItems: "center", gap: 24 }}>
              <DonutChart
                data={donutData.map((d) => ({ v: Math.round(d.v), c: d.c }))}
                size={140}
                label={moneyShort(combinedTotal)}
              />
              <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 9 }}>
                {donutData.map((d) => {
                  const p = combinedTotal > 0 ? Math.round((d.v / combinedTotal) * 100) : 0
                  return (
                    <div key={d.l} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12 }}>
                      <span style={{ width: 10, height: 10, borderRadius: 2, background: d.c, flexShrink: 0 }} />
                      <span style={{ flex: 1, color: T.ink2, fontWeight: 600 }}>{d.l}</span>
                      <span style={{ color: T.ink, fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>{money(d.v)}</span>
                      <span style={{ color: T.ink3, fontWeight: 600, fontVariantNumeric: "tabular-nums", minWidth: 34, textAlign: "right" }}>{p}%</span>
                    </div>
                  )
                })}
              </div>
            </div>
          ) : <EmptyChart title="No costs recorded" subtitle="Add payroll runs, LB leads, or overhead below" />}
        </SfCard>

        <SfCard>
          <SfCardHeader
            title="Ad-attributed leads"
            subtitle="Opportunities acquired via paid channels"
            right={<Megaphone size={14} color={T.amberDark} />}
          />
          {bySource.length ? (() => {
            // Bar encodes SPEND to match the right-aligned $ label; if the
            // tenant hasn't filled in spend yet, fall back to lead counts
            // so the chart still has shape. Previously the bar encoded
            // leads while the number read spend — visually inconsistent.
            const anySpend = bySource.some((x) => (x.spend || 0) > 0)
            const maxSpend = Math.max(1, ...bySource.map((x) => x.spend || 0))
            const maxCount = Math.max(1, ...bySource.map((x) => x.count || 0))
            return (
            <div style={{ display: "flex", flexDirection: "column", gap: 11 }}>
              {bySource.map((r, i) => {
                const barFrac = anySpend
                  ? (r.spend || 0) / maxSpend
                  : (r.count || 0) / maxCount
                return (
                  <div key={r.source}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, marginBottom: 4 }}>
                      <span style={{ flex: 1, color: T.ink, fontWeight: 600 }}>{r.source}</span>
                      <span style={{ color: T.ink3, fontSize: 11, fontVariantNumeric: "tabular-nums" }}>
                        {r.count} lead{r.count === 1 ? "" : "s"}{r.cpl != null ? ` · $${r.cpl.toFixed(0)}/lead` : ""}
                      </span>
                      <span style={{ color: T.ink, fontWeight: 700, fontVariantNumeric: "tabular-nums", minWidth: 60, textAlign: "right" }}>
                        {r.spend != null ? money(r.spend) : "—"}
                      </span>
                    </div>
                    <div style={{ height: 6, background: T.panelSoft, borderRadius: 3, overflow: "hidden" }}>
                      <div style={{ width: `${barFrac * 100}%`, height: "100%", background: [T.amberDark, T.blue, T.purple, T.teal, T.green, T.red][i % 6], borderRadius: 3 }} />
                    </div>
                  </div>
                )
              })}
            </div>
            )
          })() : <EmptyChart icon={Megaphone} title="No lead sources with attribution" subtitle="Opportunities need a source value to appear" />}
        </SfCard>
      </div>

      {/* Marketing spend by channel — canonical editor + spend surface */}
      <MarketingSpendTable
        marketingSpend={data?.marketingSpend || []}
        adsSpendBySource={m.adsSpend?.bySource || []}
        dateRange={data?.dateRange}
        onChanged={onExpensesChanged}
      />

      {/* Monthly ad-spend trend */}
      <SfCard>
        <SfCardHeader title="Ad spend · 12 months" subtitle="Monthly totals by lead-acquired date" />
        <div style={{ paddingTop: 8 }}>
          {monthlyAds.some((r) => r.spend > 0) ? (
            <BarChart
              data={monthlyAds.map((r) => Math.round(r.spend))}
              labels={monthlyAds.map((r) => r.label)}
              color={T.amberDark}
              height={180}
              valueFmt={(v) => moneyShort(v)}
            />
          ) : <EmptyChart title="No ad spend history" height={180} />}
        </div>
      </SfCard>

      {/* Overhead by category */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <SfCard>
          <SfCardHeader title="Overhead by category" subtitle="Expanded across this period" />
          {businessByCategoryEntries.length ? (
            <HBarList
              data={businessByCategoryEntries.map((r, i) => ({ l: r.l, v: r.v, c: CATEGORY_COLORS[i % CATEGORY_COLORS.length] }))}
              fmt={(v) => money(v)}
            />
          ) : <EmptyChart icon={Receipt} title="No overhead yet" subtitle="Add expenses below" />}
        </SfCard>

        <SfCard>
          <SfCardHeader title="Cost of revenue" subtitle="Payroll + ads + reimbursements as % of revenue" />
          {m.totalRevenue > 0 ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 12, paddingTop: 6 }}>
              {[
                { l: "Payroll",            v: payrollTotal,      c: T.blue },
                { l: "Ads · LeadBridge",   v: adsTotal,          c: T.amberDark },
                { l: "Job reimbursements", v: jobExpensesTotal,  c: T.teal },
                { l: "Overhead",           v: businessTotal,     c: T.purple },
              ].map((r) => {
                const pct = (r.v / m.totalRevenue) * 100
                const overflow = pct > 100
                return (
                  <div key={r.l}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, marginBottom: 4 }}>
                      <span style={{ flex: 1, color: T.ink, fontWeight: 600 }}>{r.l}</span>
                      <span style={{ color: T.ink2, fontWeight: 700, fontVariantNumeric: "tabular-nums", minWidth: 70, textAlign: "right" }}>
                        {money(r.v)}
                      </span>
                      <span style={{ color: overflow ? T.redDark : T.ink3, fontWeight: 700, fontVariantNumeric: "tabular-nums", minWidth: 56, textAlign: "right" }}>
                        {pct.toFixed(1)}%{overflow ? " ⚠" : ""}
                      </span>
                    </div>
                    <div style={{ height: 6, background: T.panelSoft, borderRadius: 3, overflow: "hidden", position: "relative" }}>
                      <div style={{ width: `${Math.min(100, pct)}%`, height: "100%", background: overflow ? T.redDark : r.c, borderRadius: 3 }} />
                    </div>
                  </div>
                )
              })}
              <div style={{ marginTop: 4, padding: "10px 12px", background: grossProfit >= 0 ? T.greenSoft : T.redSoft, border: `1px solid ${(grossProfit >= 0 ? T.green : T.red)}33`, borderRadius: 6, display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 12, color: T.ink2, fontWeight: 600 }}>Gross profit</span>
                <div style={{ flex: 1 }} />
                <span style={{ fontSize: 14, fontWeight: 700, color: grossProfit >= 0 ? T.greenDark : T.redDark, fontVariantNumeric: "tabular-nums" }}>
                  {money(grossProfit)}
                </span>
                <span style={{ fontSize: 12, color: T.ink3, fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>
                  {grossMarginPct.toFixed(1)}% margin
                </span>
              </div>
            </div>
          ) : <EmptyChart title="No revenue in this period" />}
        </SfCard>
      </div>

      {/* Recurring / one-off expenses editor */}
      <SfCard padding={false}>
        <div style={{ padding: "12px 18px", borderBottom: `1px solid ${T.borderS}`, display: "flex", alignItems: "center", gap: 10 }}>
          <Receipt size={14} color={T.ink2} />
          <div>
            <div style={{ fontSize: 13.5, fontWeight: 700, color: T.ink }}>Business expenses</div>
            <div style={{ fontSize: 11, color: T.ink3, marginTop: 1 }}>
              Overhead you pay outside jobs — rent, SaaS, insurance, marketing, …
            </div>
          </div>
          <div style={{ flex: 1 }} />
          <span style={{ fontSize: 11, color: T.ink3, fontVariantNumeric: "tabular-nums" }}>
            {recurringList.length} total · {money(businessTotal)} this period
          </span>
        </div>

        <div style={{
          display: "grid",
          gridTemplateColumns: "2fr 120px 130px 110px 110px 90px 90px 50px",
          gap: 10, padding: "10px 18px", background: T.panelAlt, borderBottom: `1px solid ${T.borderS}`,
          fontSize: 10.5, color: T.ink3, fontWeight: 700, letterSpacing: ".05em", textTransform: "uppercase",
        }}>
          <div>Name</div>
          <div>Category</div>
          <div>Cadence</div>
          <div style={{ textAlign: "right" }}>Amount</div>
          <div style={{ textAlign: "right" }}>Start</div>
          <div style={{ textAlign: "right" }}>Occurr.</div>
          <div style={{ textAlign: "right" }}>Range $</div>
          <div />
        </div>

        {recurringList.length ? recurringList.map((exp, i) => {
          const expanded = (m.expensesSummary?.businessExpanded || []).find((x) => x.id === exp.id)
          return (
            <div key={exp.id} style={{
              display: "grid",
              gridTemplateColumns: "2fr 120px 130px 110px 110px 90px 90px 50px",
              gap: 10, padding: "11px 18px", alignItems: "center",
              borderBottom: `1px solid ${T.borderS}`,
              opacity: exp.is_active === false ? 0.55 : 1,
            }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 12.5, fontWeight: 600, color: T.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {exp.name}
                </div>
                {exp.note && (
                  <div style={{ fontSize: 10.5, color: T.ink3, marginTop: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {exp.note}
                  </div>
                )}
              </div>
              <div style={{ fontSize: 12, color: T.ink2 }}>{CATEGORY_LABELS[exp.category] || exp.category}</div>
              <div style={{ fontSize: 12, color: T.ink2 }}>{CADENCE_LABELS[exp.cadence] || exp.cadence}</div>
              <div style={{ textAlign: "right", fontSize: 13, fontWeight: 700, color: T.ink, fontVariantNumeric: "tabular-nums" }}>
                {money(exp.amount)}
              </div>
              <div style={{ textAlign: "right", fontSize: 11.5, color: T.ink3, fontVariantNumeric: "tabular-nums" }}>
                {exp.start_date}
              </div>
              <div style={{ textAlign: "right", fontSize: 12, color: T.ink3, fontVariantNumeric: "tabular-nums" }}>
                {expanded?.occurrences ?? 0}
              </div>
              <div style={{ textAlign: "right", fontSize: 13, fontWeight: 700, color: expanded?.rangeAmount > 0 ? T.ink : T.ink3, fontVariantNumeric: "tabular-nums" }}>
                {expanded?.rangeAmount > 0 ? money(expanded.rangeAmount) : "—"}
              </div>
              <div style={{ textAlign: "right" }}>
                <button
                  onClick={() => handleDelete(exp.id)}
                  title="Delete"
                  style={{
                    background: "transparent", border: "none", cursor: "pointer",
                    color: T.ink3, padding: 4, borderRadius: 4,
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.color = T.redDark }}
                  onMouseLeave={(e) => { e.currentTarget.style.color = T.ink3 }}
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          )
        }) : (
          <div style={{ padding: "24px 18px", textAlign: "center", color: T.ink3, fontSize: 12.5 }}>
            No business expenses yet. Add your first one below.
          </div>
        )}

        <AddExpenseRow onSubmit={handleAdd} submitting={submitting} />
      </SfCard>
    </div>
  )
}

// ══════════════════════════════════════════════════════════════════════
// MAIN SHELL
// ══════════════════════════════════════════════════════════════════════

const AnalyticsV2 = () => {
  const { user } = useAuth()
  const { locationId, selectedLocation } = useLocationScope()
  const navigate = useNavigate()
  const [tab, setTab] = useState("overview")
  const [period, setPeriod] = useState("30d")
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [error, setError] = useState("")

  useEffect(() => {
    if (user && !isAccountOwner(user)) navigate("/dashboard", { replace: true })
  }, [user, navigate])

  const refetch = async () => {
    if (!user?.id) return
    setRefreshing(true)
    try {
      const next = await fetchEverything(user.id, period, locationId)
      setData(next)
    } catch (e) {
      setError("Failed to load analytics. Please retry.")
    } finally {
      setRefreshing(false)
    }
  }

  useEffect(() => {
    if (!user?.id || !isAccountOwner(user)) return
    let cancelled = false
    const run = async () => {
      if (data) setRefreshing(true); else setLoading(true)
      setError("")
      try {
        const next = await fetchEverything(user.id, period, locationId)
        if (!cancelled) setData(next)
      } catch (e) {
        if (!cancelled) setError("Failed to load analytics. Please retry.")
      } finally {
        if (!cancelled) { setLoading(false); setRefreshing(false) }
      }
    }
    run()
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, period, locationId])

  const metrics = useMemo(() => computeMetrics(data), [data])

  const subtitles = {
    overview:   "At-a-glance summary across all metrics",
    revenue:    "Revenue mix · growth · recurring vs. one-time · top customers",
    jobs:       "Volume · completion · cancellations · types · day-of-week",
    team:       "Worker productivity · quality · leaderboard",
    customers:  "Cohort retention · LTV · churn · acquisition",
    salary:     "Payroll cost · % of revenue · per-worker breakdown",
    conversion: "Lead → customer funnel · win rate · time-to-close",
    expenses:   "Payroll · LeadBridge ads · reimbursements · overhead",
  }

  return (
    <div className="min-h-screen" style={{ background: "var(--sf-bg, #f7f8fa)" }}>
      <MobileHeader />

      <SfPageHeader
        eyebrow={selectedLocation ? `Insights · ${selectedLocation.name}` : "Insights"}
        title="Analytics"
        subtitle={subtitles[tab]}
        actions={
          <>
            <PeriodControl value={period} onChange={setPeriod} />
            <SfButton variant="secondary" size="md" icon={Download}>Export</SfButton>
          </>
        }
        tabs={
          <>
            <SfTab active={tab === "overview"}   onClick={() => setTab("overview")}>Overview</SfTab>
            <SfTab active={tab === "revenue"}    onClick={() => setTab("revenue")}>Revenue</SfTab>
            <SfTab active={tab === "jobs"}       onClick={() => setTab("jobs")}>Jobs</SfTab>
            <SfTab active={tab === "team"}       onClick={() => setTab("team")}>Team</SfTab>
            <SfTab active={tab === "customers"}  onClick={() => setTab("customers")}>Customers</SfTab>
            <SfTab active={tab === "salary"}     onClick={() => setTab("salary")}>Salary</SfTab>
            <SfTab active={tab === "conversion"} onClick={() => setTab("conversion")}>Conversion</SfTab>
            <SfTab active={tab === "expenses"}   onClick={() => setTab("expenses")}>Expenses</SfTab>
          </>
        }
      />

      {loading && (
        <div style={{ padding: "60px 24px", textAlign: "center", color: T.ink3 }}>
          <RefreshCw size={20} className="animate-spin" style={{ display: "inline-block", marginBottom: 8 }} />
          <div style={{ fontSize: 13 }}>Loading analytics…</div>
        </div>
      )}

      {error && !loading && (
        <div style={{ margin: "14px 24px", padding: 12, background: T.redSoft, border: `1px solid ${alpha(T.red, "33")}`, borderRadius: 8, color: T.redDark, fontSize: 13 }}>
          {error}
        </div>
      )}

      {!loading && metrics && (
        <>
          {refreshing && (
            <div style={{ padding: "6px 24px 0", fontSize: 11.5, color: T.ink3, display: "flex", alignItems: "center", gap: 6 }}>
              <RefreshCw size={11} className="animate-spin" /> Refreshing…
            </div>
          )}
          {tab === "overview"   && <OverviewTab   m={metrics} data={data} />}
          {tab === "revenue"    && <RevenueTab    m={metrics} data={data} />}
          {tab === "jobs"       && <JobsTab       m={metrics} data={data} />}
          {tab === "team"       && <TeamTab       m={metrics} data={data} />}
          {tab === "customers"  && <CustomersTab  m={metrics} data={data} />}
          {tab === "salary"     && <SalaryTab     m={metrics} data={data} />}
          {tab === "conversion" && <ConversionTab m={metrics} data={data} />}
          {tab === "expenses"   && <ExpensesTab   m={metrics} data={data} onExpensesChanged={refetch} />}
        </>
      )}
    </div>
  )
}

export default AnalyticsV2
